import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  PiGoogleLogoBold,
  PiPaletteBold,
  PiSignInBold,
  PiSignOutBold,
  PiWalletBold,
  PiSidebarBold,
} from "react-icons/pi";
import { BackgroundFx } from "./BackgroundFx";
import { fetchCatalogTools, toolIsOn, type CatalogBoard } from "../lib/catalogTools";
import { EqStrip } from "./EqStrip";
import { InfoTip } from "./InfoTip";
import { Player } from "./Player";
import { SplitHandle, loadStoredPx, saveStoredPx } from "./SplitHandle";
import { compileCaption, joinCaption, mixHint } from "../lib/caption";
import {
  BPM_PRESETS,
  DEFAULT_DIALS,
  GENRE_CHIPS,
  MIX_BANKS,
  applyMasterFilters,
  customInstrumentDials,
  customVocalDials,
  flatFilters,
  hpfHz,
  instrumentDials,
  lpfHz,
  masterFilters,
  styleEqDial,
  uid,
  vocalEqDial,
  type Dial,
  type DialBank,
  type InstrumentFilters,
} from "../lib/dials";
import {
  HEALING_INSTRUMENTS,
  QUICK_INSTRUMENTS,
  instrumentVoice,
  leadInstrument,
  newCustomInstrument,
  sameInstrument,
} from "../lib/eq";
import {
  HEALING_PRESETS,
  HEALING_TONES,
  applyHealingToDials,
  isHealingStyle,
  toneByHz,
} from "../lib/healing";
import { instrumentGlyph } from "../lib/instrumentIcons";
import {
  applyAutoBox,
  AUTO_BOXES,
  DEFAULT_AUTO_BOXES,
  pickRecipe,
  type AutoBoxId,
  type AutoBoxes,
} from "../lib/autoMix";
import {
  loadMixWeights,
  saveMixWeights,
  type MixWeightKey,
  type MixWeights,
} from "../lib/mixWeights";
import { applyUiPrefs, loadUiPrefs, saveUiPrefs, TEXT_COLORS, TEXT_SIZES, type UiPrefs } from "../lib/uiPrefs";
import {
  DEFAULT_VOCAL_PROFILE,
  QUICK_VOCALS,
  VOCAL_DELIVERY,
  VOCAL_GENDER,
  VOCAL_MIC,
  VOCAL_REGISTER,
  newVocalLayer,
  sameVocalLayer,
  vocalLayerVoice,
  type VocalGender,
  type VocalMic,
  type VocalProfile,
  type VocalRegister,
} from "../lib/vocals";

import { generateSong } from "../lib/generate";
import { productionFinish } from "../lib/finish";
import { reworkLyrics as runRework } from "../lib/rework";
import { seedFromAudioFile, type SeedSong } from "../lib/seedSong";
import {
  addUploadedTracks,
  applyPulledTracks,
  compileMimicLyricBrief,
  emptyMimic,
  loadCurrentMimic,
  loadMimicShelf,
  mimicLabel,
  mimicLoaded,
  parseMimicJson,
  saveCurrentMimic,
  saveMimicShelf,
  selectedSamples,
  selectedTracks,
  serializeMimic,
  setTrackLyrics,
  setTrackSelected,
  upsertMimicShelf,
  mergeMimicShelves,
  type MusicMimic,
} from "../lib/mimic";
import {
  loadLyricPacks,
  mergeLyricPacks,
  parseLyricFiles,
  saveLyricPacks,
  type LyricPack,
} from "../lib/lyricPacks";
import { deleteRemoteMimic, fetchRemoteMimicShelf, putRemoteMimic } from "../lib/mimicRemote";
import {
  insertTag,
  MUSIC3_INPUT_MAX,
  normalizeMusic3Lyrics,
  parseSectionTagSequence,
  parseSectionTags,
  PERFORMANCE_CHIPS,
  SECTION_TAGS,
  type SectionCue,
} from "../lib/tags";
import { alignTakeLyrics } from "../lib/align";
import { karaokeFormLyrics } from "../lib/karaoke";
import { makeKaraokeBed } from "../lib/karaokeAudio";
import {
  audioDuration,
  dropTakes,
  fileStem,
  getTakeBlob,
  hydrateTakeBlobs,
  INBOX_ID,
  loadCurrentFolderId,
  loadTakes,
  moveTakes,
  rememberFinished,
  rememberKaraokeBed,
  rememberSyncedLyrics,
  rememberTake,
  renameTake,
  saveTakes,
  type FinishPresetId,
  type TakeItem,
  type TakeListen,
} from "../lib/takesStore";
import { MUSICFORGE_HF_BUCKET } from "../lib/hfBucket";
import {
  applyTokensFromHash,
  clearSession,
  fetchMe,
  getAccessToken,
  isLoggedIn,
  login as suiteLogin,
  register as suiteRegister,
  startGoogleLoginWeb,
} from "../suite/auth";
import { SUITE_MEMBER_PRICE_USD } from "../suite/access";
import type { SuiteUser, WalletSnapshot } from "../suite/types";
import { fetchWallet, startCheckout, subscribeMember } from "../suite/wallet";
import { getStoredThemeId, selectTheme } from "../themes/applyTheme";
import { BG_FX_OPTIONS, getBgFxId, setBgFxId } from "../themes/bgFx";
import { listThemes } from "../themes/registry";
import { listPickerMotions, listPickerThemes, SUITE_SKINS_EVENT } from "../themes/suiteBridge";
import type { ThemeId } from "../themes/types";

type StudioTab = "simple" | "studio" | "mimic" | "player";
type MixBank = DialBank | "seed" | `ch:${string}`;

const RAIL_KEY = "musicforge.railOpen.v1";
const MIX_OPEN_KEY = "musicforge.mixOpen.v1";
const DOCK_H_KEY = "musicforge.dockH.v1";
const CUE_H_KEY = "musicforge.cueH.v1";
const RAIL_W_KEY = "musicforge.railW.v1";
const DOCK_MIN = 96;
const CUE_MIN = 96;
const RAIL_MIN = 200;

type SongStep = "describe" | "lyrics" | "mix" | "play" | "mimic";

function LaneWeightRow({
  label,
  value,
  onChange,
  tip,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  tip: string;
}) {
  return (
    <div className="mf-lane-w">
      <span className="mf-label">
        {label} {value}
      </span>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        aria-label={`${label} lane weight`}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <InfoTip title={`${label} weight`}>
        <p>{tip}</p>
      </InfoTip>
    </div>
  );
}

const SONG_STEPS: {
  id: SongStep;
  n: string;
  label: string;
  tone: string;
  title: string;
  help: string;
  toolId: string;
}[] = [
  {
    id: "describe",
    n: "1",
    label: "Describe",
    tone: "describe",
    title: "Step 1 · Describe",
    help: "Type what the song is about in Mixing bowl. Mood, voice, and scene. This text is what Write lyrics reads.",
    toolId: "describe",
  },
  {
    id: "lyrics",
    n: "2",
    label: "Lyrics",
    tone: "lyrics",
    title: "Step 2 · Lyrics",
    help: "Write lyrics from your description, type them, or upload many .txt files. Section chips like [verse] and [chorus] tell Music 3 the form. Music 3 sings at most 4096 characters.",
    toolId: "write-lyrics",
  },
  {
    id: "mix",
    n: "3",
    label: "Mix",
    tone: "mix",
    title: "Step 3 · Mix",
    help: "Optional. Auto sliders fill Instruments, Frequencies, and lane weights from Mixing bowl. Lyrics turns the vocal lane on and opens the words. Tabs under that are the same sliders for hand tweaks.",
    toolId: "mix",
  },
  {
    id: "play",
    n: "4",
    label: "Play",
    tone: "play",
    title: "Step 4 · Play",
    help: "Make song sends lyrics + mix to MiniMax Music 3. Karaoke is game night: Make karaoke generates a music-only bed from the start, or Music only DSP-strips a take. Lyrics lock to the audio, not character count.",
    toolId: "make-song",
  },
  {
    id: "mimic",
    n: "M",
    label: "Mimic",
    tone: "mimic",
    title: "Mimic style",
    help: "Optional. Find public artist lyrics or upload your own samples, pick up to 10, Learn this style, then Use this style on the mix.",
    toolId: "mimic-style",
  },
];

export function AppShell() {
  const [tab, setTab] = useState<StudioTab>("simple");
  const [user, setUser] = useState<SuiteUser | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [status, setStatus] = useState("");
  const [bowl, setBowl] = useState(
    "A warm synth-pop night drive with a hopeful female lead and a wide final chorus.",
  );
  const [dials, setDials] = useState<Dial[]>(() =>
    DEFAULT_DIALS.map((d) => ({ ...d, filters: d.filters ? { ...d.filters } : undefined })),
  );
  const [newDial, setNewDial] = useState("");
  const [lyrics, setLyrics] = useState(`[verse]
City lights unfolding on the glass
[chorus]
We are made of sound and time`);
  const [cues, setCues] = useState<SectionCue[]>([]);
  const [duration, setDuration] = useState(60);
  const [seed, setSeed] = useState(7);
  const [randomSeed, setRandomSeed] = useState(true);
  const [seedSong, setSeedSong] = useState<SeedSong | null>(null);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captionOverride, setCaptionOverride] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [activeTakeId, setActiveTakeId] = useState<string | null>(null);
  const [playNonce, setPlayNonce] = useState(0);
  const [busy, setBusy] = useState<"lyrics" | "gen" | "mimic" | "pull" | "finish" | "karaoke" | "rework" | null>(null);
  const [previewTrackId, setPreviewTrackId] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [takes, setTakes] = useState<TakeItem[]>(() => loadTakes());
  const [mimic, setMimic] = useState<MusicMimic>(() => loadCurrentMimic());
  const [mimicShelf, setMimicShelf] = useState<MusicMimic[]>(() => loadMimicShelf());
  const [catalogBoard, setCatalogBoard] = useState<CatalogBoard | null>(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authBusy, setAuthBusy] = useState<"login" | "register" | null>(null);
  const [loginErr, setLoginErr] = useState("");
  const [skinsOpen, setSkinsOpen] = useState(false);
  const [themeId, setThemeId] = useState<ThemeId>(() => getStoredThemeId());
  const [fxId, setFxId] = useState(() => getBgFxId());
  const [pickerSkins, setPickerSkins] = useState(() => listPickerThemes(listThemes()));
  const [pickerMotions, setPickerMotions] = useState(() => listPickerMotions(BG_FX_OPTIONS));
  const [mixBank, setMixBank] = useState<MixBank>("frequency");
  const [eqTarget, setEqTarget] = useState<string>("all");
  const [vocal, setVocal] = useState<VocalProfile>(() => ({ ...DEFAULT_VOCAL_PROFILE }));
  const [mixOpen, setMixOpen] = useState(() => {
    try {
      return localStorage.getItem(MIX_OPEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [bpm, setBpm] = useState(120);
  const [genres, setGenres] = useState<string[]>([]);
  const [healingTones, setHealingTones] = useState<number[]>([]);
  const [healingPresetId, setHealingPresetId] = useState<string | null>(null);
  const [mixWeights, setMixWeights] = useState<MixWeights>(() => loadMixWeights());
  const [autoBoxes, setAutoBoxes] = useState<AutoBoxes>(DEFAULT_AUTO_BOXES);
  const [uiPrefs, setUiPrefs] = useState<UiPrefs>(() => loadUiPrefs());
  const [dockH, setDockH] = useState(() => {
    const vh = typeof window === "undefined" ? 720 : window.innerHeight;
    const cap = Math.max(DOCK_MIN, Math.round(vh * 0.45));
    return loadStoredPx(DOCK_H_KEY, Math.round(vh * 0.36), DOCK_MIN, cap);
  });
  const [cueH, setCueH] = useState(() => loadStoredPx(CUE_H_KEY, 144, CUE_MIN, 280));
  const [railW, setRailW] = useState(() => loadStoredPx(RAIL_W_KEY, 280, RAIL_MIN, 480));
  const [vpH, setVpH] = useState(() => (typeof window === "undefined" ? 720 : window.innerHeight));
  const autoMixRef = useRef({
    bowl,
    genres,
    dials,
    mixWeights,
    vocal,
    autoBoxes,
  });
  autoMixRef.current = { bowl, genres, dials, mixWeights, vocal, autoBoxes };
  const lyricsRef = useRef<HTMLTextAreaElement | null>(null);
  const seedFileRef = useRef<HTMLInputElement | null>(null);
  const lyricFileRef = useRef<HTMLInputElement | null>(null);
  const lyricFolderRef = useRef<HTMLInputElement | null>(null);
  const mimicFileRef = useRef<HTMLInputElement | null>(null);
  const lyricDestRef = useRef<"studio" | "mimic">("studio");
  const batchStopRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const [lyricPacks, setLyricPacks] = useState<LyricPack[]>(() => loadLyricPacks());
  const [activePackId, setActivePackId] = useState<string | null>(null);
  const takesRef = useRef(takes);
  takesRef.current = takes;
  const mainRef = useRef<HTMLElement | null>(null);

  const caption = useMemo(
    () =>
      compileCaption({
        dials,
        cues,
        bpm,
        genres,
        mimicName: mimic.name || mimic.targetArtist,
        mimicNotes: mimic.analysisNotes,
        mimicPrompt: compileMimicLyricBrief(mimic),
        mimicVocab: mimic.vocabularyBank,
        seed: randomSeed ? undefined : seed,
        formTags: parseSectionTagSequence(lyrics),
        vocal,
        healingTones,
        mixWeights,
        seedSongLabel: seedSong?.label,
      }),
    [dials, cues, bpm, genres, mimic, randomSeed, seed, seedSong, lyrics, vocal, healingTones, mixWeights],
  );
  const captionText = captionOverride ?? joinCaption(caption);
  const lyricCharCount = useMemo(
    () => [...normalizeMusic3Lyrics(lyrics).lyrics].length,
    [lyrics],
  );
  const lyricsOverLimit = lyricCharCount > MUSIC3_INPUT_MAX;
  const songStep: SongStep =
    tab === "simple" ? "describe" : tab === "studio" ? "lyrics" : tab === "mimic" ? "mimic" : "play";
  const readyPacks = lyricPacks.filter((p) => p.status === "ready" || p.status === "error");

  useEffect(() => {
    saveLyricPacks(lyricPacks);
  }, [lyricPacks]);

  useEffect(() => {
    void fetchCatalogTools("musicforge")
      .then(setCatalogBoard)
      .catch(() => {
        /* catalog down — keep tools on */
      });
  }, []);
  const songSteps = SONG_STEPS.filter((s) => toolIsOn(catalogBoard, s.toolId));

  useEffect(() => {
    saveCurrentMimic(mimic);
  }, [mimic]);

  useEffect(() => {
    saveMimicShelf(mimicShelf);
  }, [mimicShelf]);

  const syncMimicShelfFromBucket = async () => {
    if (!isLoggedIn()) return;
    try {
      const remote = await fetchRemoteMimicShelf();
      setMimicShelf((local) => {
        const next = mergeMimicShelves(local, remote);
        saveMimicShelf(next);
        return next;
      });
      if (remote.length) setStatus(`Loaded ${remote.length} mimic${remote.length === 1 ? "" : "s"} from your bucket.`);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
    }
  };

  const pushMimicToBucket = async (m: MusicMimic): Promise<string | null> => {
    if (!isLoggedIn()) return null;
    try {
      return await putRemoteMimic(m);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
      return null;
    }
  };

  useEffect(() => {
    applyTokensFromHash();
    if (!isLoggedIn()) return;
    fetchMe()
      .then((u) => {
        setUser(u);
        void syncMimicShelfFromBucket();
      })
      .catch(() => setUser(null));
    fetchWallet()
      .then(setWallet)
      .catch(() => setWallet(null));
  }, []);

  useEffect(() => {
    let live = true;
    void hydrateTakeBlobs(loadTakes()).then((next) => {
      if (!live) return;
      setTakes(next);
      const first = next.find((t) => t.objectUrl);
      if (first?.objectUrl) {
        setAudioUrl(first.objectUrl);
        setActiveTakeId(first.id);
      }
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(RAIL_KEY, railOpen ? "1" : "0");
    } catch {
      /* */
    }
  }, [railOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(MIX_OPEN_KEY, mixOpen ? "1" : "0");
    } catch {
      /* */
    }
  }, [mixOpen]);

  useEffect(() => saveStoredPx(DOCK_H_KEY, dockH), [dockH]);
  useEffect(() => saveStoredPx(CUE_H_KEY, cueH), [cueH]);
  useEffect(() => saveStoredPx(RAIL_W_KEY, railW), [railW]);

  useEffect(() => {
    const capDock = () => {
      const h = window.innerHeight;
      setVpH(h);
      const box = mainRef.current?.clientHeight ?? h;
      const cap = Math.max(DOCK_MIN, Math.round(box * 0.72));
      setDockH((v) => Math.min(v, cap));
    };
    capDock();
    window.addEventListener("resize", capDock);
    return () => window.removeEventListener("resize", capDock);
  }, []);

  const dockMax = Math.max(
    DOCK_MIN,
    Math.round((mainRef.current?.clientHeight ?? vpH) * 0.72),
  );
  const cueMax = Math.max(CUE_MIN, Math.round(vpH * 0.4));

  const phoneLayout = () => window.matchMedia("(max-width: 900px)").matches;
  const mixShown = mixOpen && tab !== "mimic" && tab !== "player";

  const openMix = (bank?: MixBank) => {
    if (bank) setMixBank(bank);
    setMixOpen(true);
    if (phoneLayout()) return;
    const mainH = mainRef.current?.clientHeight ?? vpH;
    setDockH((h) => Math.max(h, Math.round(mainH * 0.55), 280));
  };

  const closeOverlays = useCallback(() => {
    setWalletOpen(false);
    setLoginOpen(false);
    setRailOpen(false);
    setSkinsOpen(false);
  }, []);

  const openLogin = useCallback(() => {
    setLoginOpen(true);
    setWalletOpen(false);
    setSkinsOpen(false);
    setLoginErr("");
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeOverlays();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeOverlays]);

  useEffect(() => {
    const sync = () => {
      setPickerSkins(listPickerThemes(listThemes()));
      setPickerMotions(listPickerMotions(BG_FX_OPTIONS));
      setThemeId(getStoredThemeId());
      setFxId(getBgFxId());
      applyUiPrefs(uiPrefs);
    };
    window.addEventListener(SUITE_SKINS_EVENT, sync);
    const t = window.setTimeout(sync, 400);
    return () => {
      window.removeEventListener(SUITE_SKINS_EVENT, sync);
      window.clearTimeout(t);
    };
  }, [uiPrefs]);

  useEffect(() => {
    applyUiPrefs(uiPrefs);
  }, [uiPrefs]);

  const setDial = (id: string, value: number) => {
    setCaptionOverride(null);
    setDials((ds) => ds.map((d) => (d.id === id ? { ...d, value } : d)));
  };

  const setMixWeight = (key: MixWeightKey, value: number) => {
    setCaptionOverride(null);
    setMixWeights((cur) => {
      const next = { ...cur, [key]: value };
      saveMixWeights(next);
      return next;
    });
    if (key === "mimic") {
      setDials((ds) => ds.map((d) => (d.id === "mimicwt" ? { ...d, value } : d)));
    }
  };

  const toggleAutoBox = (box: AutoBoxId) => {
    const cur = autoMixRef.current;
    const on = !cur.autoBoxes[box];
    const nextBoxes = { ...cur.autoBoxes, [box]: on };
    const applied = applyAutoBox({
      box,
      on,
      bowl: cur.bowl,
      genres: cur.genres,
      dials: cur.dials,
      mixWeights: cur.mixWeights,
      vocal: cur.vocal,
      uid,
    });
    autoMixRef.current = {
      bowl: cur.bowl,
      genres: applied.genres,
      dials: applied.dials,
      mixWeights: applied.mixWeights,
      vocal: applied.vocal,
      autoBoxes: nextBoxes,
    };
    setAutoBoxes(nextBoxes);
    setCaptionOverride(null);
    setDials(applied.dials);
    setMixWeights(applied.mixWeights);
    saveMixWeights(applied.mixWeights);
    setVocal(applied.vocal);
    setGenres(applied.genres);
    if (box === "lyrics" && on) setTab("studio");
    openMix(applied.openBank);
    setStatus(applied.status);
  };

  const armMimicLane = () => {
    setMixWeights((cur) => {
      if (cur.mimic > 0) return cur;
      const next = { ...cur, mimic: 70 };
      saveMixWeights(next);
      return next;
    });
    setDials((ds) =>
      ds.map((d) => (d.id === "mimicwt" && d.value <= 0 ? { ...d, value: 70 } : d)),
    );
  };

  const writeEq = (target: string, next: InstrumentFilters) => {
    setCaptionOverride(null);
    if (target === "all") {
      setDials((ds) => applyMasterFilters(ds, next));
      return;
    }
    if (target === "style") {
      setDials((ds) => ds.map((d) => (d.id === "styleeq" ? { ...d, filters: next } : d)));
      return;
    }
    if (target === "vocals") {
      setDials((ds) => ds.map((d) => (d.id === "vocaleq" ? { ...d, filters: next } : d)));
      return;
    }
    setDials((ds) => ds.map((d) => (d.id === target ? { ...d, filters: next } : d)));
  };

  const eqFiltersFor = (target: string): InstrumentFilters => {
    if (target === "all") return masterFilters(dials);
    if (target === "style") return styleEqDial(dials)?.filters ?? flatFilters();
    if (target === "vocals") return vocalEqDial(dials)?.filters ?? flatFilters();
    return dials.find((d) => d.id === target)?.filters ?? flatFilters();
  };

  const patchVocal = (partial: Partial<VocalProfile>) => {
    setCaptionOverride(null);
    setVocal((cur) => ({ ...cur, ...partial }));
  };

  const addDial = (raw?: string) => {
    const label = (raw ?? newDial).trim().slice(0, 48);
    if (!label) {
      setStatus("Name an instrument, then ＋ Add dial");
      return;
    }
    const existing = dials.find((d) => d.bank === "instruments" && sameInstrument(d.label, label));
    if (existing) {
      setCaptionOverride(null);
      setEqTarget(existing.id);
      openMix(`ch:${existing.id}`);
      setNewDial("");
      setStatus(`Opened ${existing.label} — raise level or EQ so it speaks in the mix`);
      return;
    }
    const created = newCustomInstrument(label, uid);
    setCaptionOverride(null);
    setDials((ds) => [...ds, created]);
    setNewDial("");
    setEqTarget(created.id);
    openMix(`ch:${created.id}`);
    setStatus(`Added ${created.label} as a featured instrument with its own filters`);
  };

  const toggleVocalLayer = (name: string) => {
    const existing = dials.find((d) => d.bank === "vocals" && d.custom && sameVocalLayer(d.label, name));
    if (existing) {
      removeDial(existing.id);
      setStatus(`Removed ${existing.label} from the vocal mix`);
      return;
    }
    const created = newVocalLayer(name, uid);
    setCaptionOverride(null);
    setDials((ds) => [...ds, created]);
    setEqTarget(created.id);
    openMix("vocals");
    setStatus(`Added ${created.label} as a vocal layer`);
  };

  const removeDial = (id: string) => {
    setCaptionOverride(null);
    setDials((ds) => ds.filter((d) => d.id !== id));
    if (eqTarget === id) setEqTarget("all");
    if (mixBank === `ch:${id}`) {
      const gone = dials.find((d) => d.id === id);
      setMixBank(gone?.bank === "vocals" ? "vocals" : "instruments");
    }
  };

  const makeLead = (id: string) => {
    const inst = dials.find((d) => d.id === id);
    setCaptionOverride(null);
    setDials((ds) => leadInstrument(ds, id));
    setEqTarget(id);
    openMix(`ch:${id}`);
    setStatus(`${inst?.label || "Instrument"} is lead — supporting bed ducked`);
  };

  const jumpEq = (id: string) => {
    setEqTarget(id);
    openMix("frequency");
  };

  const dialReadout = (d: Dial) => {
    if (d.id === "hpf") return `${hpfHz(d.value)} Hz`;
    if (d.id === "lpf") return `${lpfHz(d.value)} Hz`;
    return String(d.value);
  };

  const toggleHealingTone = (hz: number) => {
    setCaptionOverride(null);
    setHealingPresetId(null);
    const on = !healingTones.includes(hz);
    const next = on ? [...healingTones, hz].sort((a, b) => a - b) : healingTones.filter((x) => x !== hz);
    setHealingTones(next);
    if (on && !genres.includes("healing")) setGenres(["healing", ...genres]);
    if (on && mixWeights.healing <= 0) setMixWeight("healing", 55);
  };

  const applyHealingPreset = (id: string) => {
    const preset = HEALING_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    setHealingPresetId(preset.id);
    setHealingTones(preset.tones);
    setBowl(preset.bowl);
    setLyrics(preset.lyrics);
    setGenres((gs) => {
      const extra = preset.genres.filter((g) => !gs.includes(g));
      return extra.length ? [...gs, ...extra] : gs;
    });
    setCaptionOverride(null);
    setDials((ds) => applyHealingToDials(ds, preset, uid));
    if (mixWeights.healing < 70) setMixWeight("healing", 70);
    setEqTarget("all");
    openMix("frequency");
    setStatus(`Healing preset · ${preset.label} · ${preset.tones.join(" / ")} Hz · other lanes kept`);
  };

  const dialRow = (d: Dial) => (
    <div className="mf-dial-row" key={d.id}>
      <span className="mf-dial-lab">
        <span className="mf-inst-ico" aria-hidden>
          {instrumentGlyph(d.label)}
        </span>
        {d.label}
      </span>
      <input
        type="range"
        min={0}
        max={100}
        value={d.value}
        aria-label={d.label}
        onChange={(e) => setDial(d.id, Number(e.target.value))}
      />
      <b>{dialReadout(d)}</b>
      {d.bank === "instruments" || (d.bank === "vocals" && d.custom) ? (
        <button
          type="button"
          className="mf-eq-jump"
          onClick={() => jumpEq(d.id)}
          title={`Equalizer for ${d.label}`}
        >
          EQ
        </button>
      ) : (
        <span />
      )}
      {d.custom ? (
        <button type="button" className="mf-x" onClick={() => removeDial(d.id)} aria-label={`Remove ${d.label}`}>
          ✕
        </button>
      ) : (
        <span />
      )}
    </div>
  );

  const insertSection = (tag: string) => {
    const el = lyricsRef.current;
    const pos = el ? el.selectionStart : lyrics.length;
    const next = insertTag(lyrics, tag, pos);
    setLyrics(next);
  };

  const toggleCue = (tag: string, cue: string) => {
    setCaptionOverride(null);
    setCues((cs) => {
      const hit = cs.find((c) => c.tag === tag);
      if (hit?.cue === cue) return cs.filter((c) => c.tag !== tag);
      return [...cs.filter((c) => c.tag !== tag), { tag, cue }];
    });
  };

  const needAuth = () => {
    if (isLoggedIn()) return true;
    setStatus("Sign in with VividLex (email) or Google — lyrics, Make song, and Production finish need a suite login.");
    openLogin();
    return false;
  };

  const sessionAfterAuth = async (email?: string) => {
    setLoginOpen(false);
    setAuthPass("");
    setLoginErr("");
    try {
      setUser(await fetchMe());
    } catch {
      if (email) setUser({ id: "", email, full_name: null });
    }
    try {
      setWallet(await fetchWallet());
    } catch {
      setWallet(null);
    }
    setStatus(`Signed in as ${email || "suite account"}.`);
    void syncMimicShelfFromBucket();
  };

  const onEmailLogin = async () => {
    setAuthBusy("login");
    setLoginErr("");
    try {
      const tokens = await suiteLogin({ email: authEmail, password: authPass });
      await sessionAfterAuth(tokens.user?.email || authEmail);
    } catch (e) {
      setLoginErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAuthBusy(null);
    }
  };

  const onEmailRegister = async () => {
    setAuthBusy("register");
    setLoginErr("");
    try {
      const tokens = await suiteRegister({ email: authEmail, password: authPass });
      await sessionAfterAuth(tokens.user?.email || authEmail);
    } catch (e) {
      setLoginErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAuthBusy(null);
    }
  };

  const promptLyrics = async (picks = 1): Promise<boolean> => {
    const prompt = bowl.trim();
    if (!prompt) {
      setTab("simple");
      setStatus("Step 1: type a song in Mixing bowl, then Write lyrics.");
      return false;
    }
    if (!needAuth()) return false;
    if (mimicLoaded(mimic)) armMimicLane();
    const useWriter = "glimmer" as const;
    setBusy("lyrics");
    setStatus(
      picks > 1 ? `Writing ${picks} lyric sheets from Mixing bowl…` : "Writing lyrics from Mixing bowl…",
    );
    try {
      const tok = getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      };
      const lyricBody = (extra: Record<string, unknown> = {}) =>
        JSON.stringify({
          prompt,
          description: `${prompt}\n\n${mixHint({ bpm, genres, healingTones, mixWeights, vocal })}`,
          duration,
          writer: useWriter,
          vocal: {
            delivery: vocal.delivery,
            gender: vocal.gender,
            register: vocal.register,
            mic: vocal.mic,
          },
          mimicPrompt: compileMimicLyricBrief(mimic) || undefined,
          ...extra,
        });
      const parseLyric = async (res: Response) => {
        const raw = await res.text();
        let data: { lyrics?: string; title?: string; error?: string; wallet_usd?: number } = {};
        try {
          data = JSON.parse(raw) as typeof data;
        } catch {
          throw new Error(raw.slice(0, 280) || `Lyrics failed (${res.status})`);
        }
        if (!res.ok) throw new Error(data.error || `Lyrics failed (${res.status})`);
        if (!data.lyrics?.trim()) throw new Error("No lyrics returned.");
        if (typeof data.wallet_usd === "number") {
          setWallet((w) => (w ? { ...w, balanceUsd: data.wallet_usd! } : w));
        }
        return data;
      };

      if (picks > 1) {
        const gens: { id: string; title?: string; lyrics: string }[] = [];
        for (let i = 0; i < picks; i++) {
          setStatus(`Writing ${i + 1}/${picks}…`);
          const data = await parseLyric(
            await fetch("/api/lyrics", {
              method: "POST",
              headers,
              body: lyricBody({ variation: i + 1, variations: picks }),
            }),
          );
          gens.push({ id: `g${i + 1}`, title: data.title, lyrics: data.lyrics! });
        }
        if (gens.length < 2) throw new Error("Need at least two lyric sheets for Mix pick.");
        setStatus("Jev picking…");
        const pickRes = await fetch("/api/lyrics/pick", {
          method: "POST",
          headers,
          body: JSON.stringify({
            prompt,
            description: `${prompt}\n\n${mixHint({ bpm, genres, healingTones, mixWeights, vocal })}`,
            duration,
            vocal: {
              delivery: vocal.delivery,
              gender: vocal.gender,
              register: vocal.register,
            },
            gens,
          }),
        });
        const pickRaw = await pickRes.text();
        let pick: {
          choice?: string;
          confidence?: number;
          lyrics?: string;
          title?: string;
          none?: boolean;
          error?: string;
        } = {};
        try {
          pick = JSON.parse(pickRaw) as typeof pick;
        } catch {
          throw new Error(pickRaw.slice(0, 280) || `Jev pick failed (${pickRes.status})`);
        }
        if (!pickRes.ok) throw new Error(pick.error || `Jev pick failed (${pickRes.status})`);
        if (pick.none || !pick.lyrics?.trim()) {
          setStatus("Jev kept none — edit the bowl and Pick 10 again.");
          return false;
        }
        setLyrics(pick.lyrics);
        setTab("studio");
        const conf = typeof pick.confidence === "number" ? pick.confidence.toFixed(2) : "";
        setStatus(
          pick.title
            ? `Jev picked ${pick.choice} (${conf}) · ${pick.title}`
            : `Jev picked ${pick.choice} (${conf})`,
        );
        return true;
      }

      const data = await parseLyric(
        await fetch("/api/lyrics", { method: "POST", headers, body: lyricBody() }),
      );
      setLyrics(data.lyrics!);
      setTab("studio");
      setStatus(data.title ? `Lyrics · ${data.title}` : "Lyrics filled from Mixing bowl.");
      return true;
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const generate = async (opts?: {
    keepSeed?: boolean;
    folderId?: string;
    lyrics?: string;
    title?: string;
    packId?: string;
    karaoke?: boolean;
  }): Promise<boolean> => {
    if (!needAuth()) return false;
    const body = (opts?.lyrics ?? lyrics).trim();
    if (!body) {
      setTab("studio");
      setStatus("Step 2: add lyrics (Write lyrics, type them, or upload files), then Make song.");
      return false;
    }
    const useSeed = opts?.keepSeed
      ? seed
      : randomSeed
        ? Math.floor(Math.random() * 1_000_000)
        : seedSong?.seed ?? seed;
    setSeed(useSeed);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setBusy("gen");
    setTab("player");
    if (opts?.lyrics) setLyrics(opts.lyrics);
    if (opts?.packId) {
      setActivePackId(opts.packId);
      setLyricPacks((cur) => cur.map((p) => (p.id === opts.packId ? { ...p, status: "making", error: undefined } : p)));
    }
    const karaoke = Boolean(opts?.karaoke);
    const titleBase = (opts?.title || bowl).slice(0, 48) || "Untitled";
    const title = karaoke ? `${titleBase.replace(/\s*karaoke$/i, "")} karaoke`.slice(0, 48) : titleBase;
    if (mimicLoaded(mimic)) armMimicLane();
    const sendLyrics = karaoke ? karaokeFormLyrics(body) : body;
    const genCaption =
      captionOverride && !karaoke
        ? captionOverride
        : joinCaption(
            compileCaption({
              dials,
              cues,
              bpm,
              genres,
              mimicName: mimic.name || mimic.targetArtist,
              mimicNotes: mimic.analysisNotes,
              mimicPrompt: compileMimicLyricBrief(mimic),
              mimicVocab: mimic.vocabularyBank,
              seed: randomSeed ? undefined : seed,
              formTags: parseSectionTagSequence(sendLyrics),
              vocal: karaoke ? { ...vocal, delivery: "instrumental" } : vocal,
              healingTones,
              mixWeights: karaoke
                ? { ...mixWeights, vocals: 0 }
                : mimicLoaded(mimic) && mixWeights.mimic <= 0
                  ? { ...mixWeights, mimic: 70 }
                  : mixWeights,
              seedSongLabel: seedSong?.label,
              karaoke,
            }),
          );
    const sendingChars = [...normalizeMusic3Lyrics(sendLyrics).lyrics].length;
    const markPack = (status: LyricPack["status"], error?: string) => {
      if (!opts?.packId) return;
      setLyricPacks((cur) =>
        cur.map((p) => (p.id === opts.packId ? { ...p, status, error } : p)),
      );
    };
    try {
      setStatus(
        karaoke
          ? sendingChars > MUSIC3_INPUT_MAX
            ? "Making karaoke bed on MiniMax Music 3… Lyrics trimmed to 4096 characters."
            : "Making karaoke bed on MiniMax Music 3 — music only, no lead vocal…"
          : sendingChars > MUSIC3_INPUT_MAX
            ? "Making song on MiniMax Music 3… Lyrics trimmed to 4096 characters."
            : "Making song on MiniMax Music 3…",
      );
      const result = await generateSong({
        lyrics: sendLyrics,
        caption: genCaption,
        duration,
        seed: useSeed,
        signal: ac.signal,
        onProgress: (p) => setStatus(p.message),
      });
      if (result.waking) {
        const msg = result.error || "Waking Music 3 box — first GPU boot can take several minutes. Retry shortly.";
        setStatus(msg);
        markPack("error", msg);
        return false;
      }
      if (result.error) {
        setStatus(result.error);
        markPack("error", result.error);
        return false;
      }
      const url = URL.createObjectURL(result.blob);
      const take: TakeItem = {
        id: uid("take"),
        title,
        createdAt: new Date().toISOString(),
        duration,
        seed: useSeed,
        lyrics: body,
        caption: captionText,
        objectUrl: url,
        contentType: result.contentType,
        folderId: opts?.folderId || loadCurrentFolderId() || INBOX_ID,
        bytes: result.blob.size,
        kind: "gen",
        karaoke: karaoke ? { at: new Date().toISOString(), source: "gen" } : undefined,
      };
      setAudioUrl(url);
      setActiveTakeId(take.id);
      setTakes(await rememberTake(takesRef.current, take, result.blob));
      setPlayNonce((n) => n + 1);
      setStatus(karaoke ? `Karaoke bed ready · ${title}` : `Song ready · ${title}`);
      markPack("done");
      fetchWallet().then(setWallet).catch(() => undefined);
      // Karaoke sync needs a sung vocal. Skip instrumental karaoke beds.
      if (!karaoke && body.trim()) {
        void (async () => {
          setStatus(`Song ready · ${title} — syncing karaoke lyrics…`);
          const aligned = await alignTakeLyrics({
            audio: result.blob,
            lyrics: body,
            onProgress: (p) => setStatus(`${title}: ${p.message}`),
          });
          if (aligned.ok) {
            setTakes((cur) => rememberSyncedLyrics(cur, take.id, aligned.result.syncedLyrics));
            setStatus(
              `Song ready · ${title} · karaoke synced (${aligned.result.matchedWords ?? "?"} words)`,
            );
          } else {
            setStatus(`Song ready · ${title} · karaoke sync skipped: ${aligned.error}`);
          }
        })();
      }
      return true;
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") {
        setStatus("Stopped.");
        markPack("ready");
      } else {
        const msg = e instanceof Error ? e.message : String(e);
        setStatus(msg);
        markPack("error", msg);
      }
      return false;
    } finally {
      setBusy(null);
    }
  };

  const goStep = (id: SongStep) => {
    if (id === "describe") setTab("simple");
    else if (id === "lyrics") {
      setTab("studio");
      setMixOpen(false);
      setDockH(120);
    } else if (id === "mimic") setTab("mimic");
    else if (id === "play") setTab("player");
    else if (mixOpen && tab !== "player" && tab !== "mimic") {
      setMixOpen(false);
    } else {
      if (tab === "player" || tab === "mimic") setTab("studio");
      openMix("frequency");
    }
  };

  const loadPack = (pack: LyricPack) => {
    setLyrics(pack.lyrics);
    setActivePackId(pack.id);
    setTab("studio");
    setStatus(`Loaded “${pack.title}” · ${pack.source}`);
  };

  const pickLyricFiles = (dest: "studio" | "mimic", folder = false) => {
    lyricDestRef.current = dest;
    const el = folder ? lyricFolderRef.current : lyricFileRef.current;
    el?.click();
  };

  const onLyricFiles = async (list: FileList | null) => {
    const files = [...(list || [])];
    if (!files.length) return;
    const packs = await parseLyricFiles(files);
    if (!packs.length) {
      setStatus("No lyric text files in that drop (.txt, .lrc, .md, .json).");
      return;
    }
    if (lyricDestRef.current === "mimic") {
      const next = addUploadedTracks(mimic, packs);
      setMimic(next);
      setPreviewTrackId(next.tracks[0]?.id || null);
      setTab("mimic");
      armMimicLane();
      setStatus(`Uploaded ${packs.length} lyric sample${packs.length === 1 ? "" : "s"}. Pick up to 10, then Learn this style.`);
      return;
    }
    setLyricPacks((cur) => mergeLyricPacks(cur, packs));
    const first = packs[0];
    if (first) {
      setLyrics(first.lyrics);
      setActivePackId(first.id);
    }
    setTab("studio");
    setStatus(
      packs.length === 1
        ? `Loaded “${packs[0].title}”. Edit if you want, then Make this song.`
        : `Loaded ${packs.length} songs. Pick one, or Make all songs.`,
    );
  };

  const makeAllSongs = async () => {
    const queue = lyricPacks.filter((p) => p.status === "ready" || p.status === "error");
    if (!queue.length) {
      setStatus("Upload lyric files first, or Make this song for the lyrics on screen.");
      return;
    }
    if (!needAuth()) return;
    batchStopRef.current = false;
    let made = 0;
    for (let i = 0; i < queue.length; i++) {
      if (batchStopRef.current) {
        setStatus(`Stopped after ${made} of ${queue.length}.`);
        break;
      }
      const pack = queue[i];
      setStatus(`Making ${i + 1} of ${queue.length} · ${pack.title}`);
      const ok = await generate({ lyrics: pack.lyrics, title: pack.title, packId: pack.id });
      if (ok) made += 1;
      if (batchStopRef.current || !isLoggedIn()) break;
    }
    if (!batchStopRef.current) setStatus(`Made ${made} of ${queue.length} songs.`);
  };

  const stopWork = () => {
    batchStopRef.current = true;
    abortRef.current?.abort();
  };

  const playTake = (t: TakeItem) => {
    const url =
      t.listen === "finished" && t.finishedUrl ? t.finishedUrl : t.objectUrl;
    if (url) {
      setAudioUrl(url);
      setActiveTakeId(t.id);
      setPlayNonce((n) => n + 1);
      setStatus(`Playing “${t.title}”`);
      return;
    }
    void hydrateTakeBlobs([t]).then((rows) => {
      const hit = rows[0];
      if (!hit?.objectUrl) {
        setStatus("That take has no audio on this machine.");
        return;
      }
      setTakes((cur) => cur.map((x) => (x.id === hit.id ? hit : x)));
      setAudioUrl(hit.objectUrl);
      setActiveTakeId(hit.id);
      setPlayNonce((n) => n + 1);
    });
  };

  const loadTake = (t: TakeItem) => {
    playTake(t);
    if (t.lyrics) setLyrics(t.lyrics);
    if (t.caption) setCaptionOverride(t.caption);
    setSeed(t.seed);
    setRandomSeed(false);
    setTab("studio");
    setStatus(`Loaded “${t.title}” into studio`);
  };

  const deleteTakes = (ids: string[]) => {
    const gone = new Set(ids);
    void dropTakes(takesRef.current, ids).then((next) => {
      setTakes(next);
      if (activeTakeId && gone.has(activeTakeId)) {
        const pick = next.find((x) => x.objectUrl) || next[0] || null;
        setActiveTakeId(pick?.id || null);
        setAudioUrl(pick?.objectUrl || null);
      }
    });
  };

  const listenTake = (take: TakeItem, listen: TakeListen) => {
    const url = listen === "finished" ? take.finishedUrl : take.objectUrl;
    if (!url) return;
    const next = takesRef.current.map((t) => (t.id === take.id ? { ...t, listen } : t));
    saveTakes(next);
    setTakes(next);
    setActiveTakeId(take.id);
    setAudioUrl(url);
  };

  const finishTake = async (take: TakeItem, preset: FinishPresetId) => {
    if (!needAuth()) return;
    if (!wallet?.productionFinish) {
      setStatus("Production finish is off for this account. Turn it on in AccessHub → Users → tool access.");
      return;
    }
    const blob = (await getTakeBlob(take.id)) || (take.objectUrl ? await fetch(take.objectUrl).then((r) => r.blob()) : null);
    if (!blob) {
      setStatus("No audio on that take to finish.");
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setBusy("finish");
    setTab("player");
    setActiveTakeId(take.id);
    setStatus("Production finish — mastering for upload…");
    try {
      const result = await productionFinish({
        audio: blob,
        preset,
        signal: ac.signal,
        onProgress: (p) => setStatus(p.message),
      });
      if (!result.ok) {
        setStatus(result.error);
        return;
      }
      const next = await rememberFinished(takesRef.current, take.id, result.result.blob, {
        preset: result.result.preset,
        at: new Date().toISOString(),
        lufs: result.result.lufs,
        truePeakDb: result.result.truePeakDb,
      });
      setTakes(next);
      const done = next.find((t) => t.id === take.id);
      if (done?.finishedUrl) {
        setAudioUrl(done.finishedUrl);
        setPlayNonce((n) => n + 1);
      }
      const lufs = result.result.lufs != null ? ` · ${result.result.lufs} LUFS` : "";
      setStatus(`Finished for ${result.result.preset}${lufs}. Original kept.`);
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") {
        setStatus("Stopped.");
      } else {
        setStatus(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  };

  const reworkTake = async (take: TakeItem, nextLyrics: string) => {
    if (!needAuth()) return;
    const lyrics = nextLyrics.trim();
    if (!lyrics) {
      setStatus("Add lyrics before Rework.");
      return;
    }
    const blob = (await getTakeBlob(take.id)) || (take.objectUrl ? await fetch(take.objectUrl).then((r) => r.blob()) : null);
    if (!blob) {
      setStatus("No audio on that take to rework.");
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setBusy("rework");
    setTab("player");
    setActiveTakeId(take.id);
    setLyrics(lyrics);
    setStatus("Reworking lyrics on ACE-Step…");
    try {
      const result = await runRework({
        audio: blob,
        lyrics,
        caption: take.caption || captionText,
        signal: ac.signal,
        onProgress: (p) => setStatus(p.message),
      });
      if (!result.ok) {
        setStatus(
          result.waking
            ? result.error || "Waking ACE-Step box — first GPU boot can take several minutes."
            : result.error,
        );
        return;
      }
      const url = URL.createObjectURL(result.blob);
      const child: TakeItem = {
        id: uid("take"),
        title: `${take.title.replace(/\s*\(rework\)\s*$/i, "")} (rework)`.slice(0, 48),
        createdAt: new Date().toISOString(),
        duration: take.duration,
        seed: take.seed,
        lyrics,
        caption: take.caption || captionText,
        objectUrl: url,
        contentType: result.contentType,
        folderId: take.folderId || INBOX_ID,
        bytes: result.blob.size,
        kind: "rework",
        parentId: take.id,
      };
      setAudioUrl(url);
      setActiveTakeId(child.id);
      setTakes(await rememberTake(takesRef.current, child, result.blob));
      setPlayNonce((n) => n + 1);
      setStatus(`Rework ready · ${child.title}. Original kept.`);
      fetchWallet().then(setWallet).catch(() => undefined);
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") {
        setStatus("Stopped.");
      } else {
        setStatus(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  };

  const syncKaraokeLyrics = async (take: TakeItem) => {
    const sheet = (take.lyrics || lyrics || "").trim();
    if (!sheet) {
      setStatus("Add lyrics before syncing karaoke timing.");
      return;
    }
    const blob = (await getTakeBlob(take.id)) || null;
    if (!blob?.size) {
      setStatus("Open a take with audio first, then Sync lyrics.");
      return;
    }
    setBusy("karaoke");
    setStatus("Syncing lyrics to the mix (Whisper)…");
    try {
      const aligned = await alignTakeLyrics({
        audio: blob,
        lyrics: sheet,
        onProgress: (p) => setStatus(p.message),
      });
      if (!aligned.ok) {
        setStatus(aligned.error);
        return;
      }
      setTakes((cur) => rememberSyncedLyrics(cur, take.id, aligned.result.syncedLyrics));
      setStatus(
        `Karaoke lyrics synced · ${aligned.result.matchedWords ?? "?"} words matched`,
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Lyric sync failed.");
    } finally {
      setBusy(null);
    }
  };

  const stripKaraokeBed = async (take: TakeItem) => {
    if (take.karaoke?.source === "gen") {
      setStatus("This take is already a karaoke bed (music only).");
      return;
    }
    const blob =
      (await getTakeBlob(take.id)) || (take.objectUrl ? await fetch(take.objectUrl).then((r) => r.blob()) : null);
    if (!blob) {
      setStatus("No audio on this take to strip vocals from.");
      return;
    }
    setBusy("karaoke");
    setStatus("DSP: pulling the lead vocal for a music-only karaoke bed…");
    try {
      const bed = await makeKaraokeBed(blob);
      const next = await rememberKaraokeBed(takesRef.current, take.id, bed, "dsp");
      setTakes(next);
      const done = next.find((t) => t.id === take.id);
      if (done?.karaokeBedUrl) setPlayNonce((n) => n + 1);
      setStatus("Music-only karaoke bed ready. Karaoke plays this, not the sung take.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Could not make a karaoke bed.");
    } finally {
      setBusy(null);
    }
  };

  const importFiles = async (files: File[], folderId: string) => {
    setTab("player");
    let list = takesRef.current;
    let last: TakeItem | null = null;
    for (const file of files) {
      const url = URL.createObjectURL(file);
      const dur = await audioDuration(url);
      const take: TakeItem = {
        id: uid("take"),
        title: fileStem(file.name),
        createdAt: new Date().toISOString(),
        duration: dur,
        seed: 0,
        objectUrl: url,
        contentType: file.type || "audio/wav",
        folderId: folderId || INBOX_ID,
        bytes: file.size,
        kind: "import",
      };
      list = await rememberTake(list, take, file);
      last = take;
    }
    setTakes(list);
    if (last?.objectUrl) {
      setAudioUrl(last.objectUrl);
      setActiveTakeId(last.id);
      setPlayNonce((n) => n + 1);
    }
    setStatus(`Imported ${files.length} file${files.length === 1 ? "" : "s"}.`);
  };

  const pullLyrics = async (from?: MusicMimic): Promise<MusicMimic | null> => {
    if (!needAuth()) return null;
    const src = from || mimic;
    const artist = src.targetArtist.trim();
    if (!artist) {
      setStatus("Name an artist, then Find artist lyrics.");
      return null;
    }
    setBusy("pull");
    setStatus(`Pulling lyrics for ${artist}…`);
    try {
      const res = await fetch("/api/mimic/pull", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ artist, limit: 10 }),
      });
      const data = (await res.json()) as {
        artist?: string;
        era?: string;
        tracks?: { id: string; title: string; artist: string; album?: string; year?: number; lyrics: string }[];
        source?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(data.error || "Pull failed");
      const tracks = data.tracks || [];
      if (!tracks.length) throw new Error("No lyrics returned for that artist.");
      let next = applyPulledTracks(src, tracks, { artist: data.artist, era: data.era });
      const pulled = (data.artist || artist).trim().toLowerCase();
      const prev = (src.targetArtist || src.name).trim().toLowerCase();
      if (prev && pulled && prev !== pulled) next = { ...next, id: `mimic-${Date.now()}` };
      setMimic(next);
      setPreviewTrackId(next.tracks[0]?.id || null);
      setPasteOpen(false);
      armMimicLane();
      setStatus(
        `Found ${tracks.length} lyric tracks${data.source ? ` · ${data.source}` : ""}. Select up to 10, then Learn this style.`,
      );
      return next;
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const analyzeMimic = async (from?: MusicMimic) => {
    if (!needAuth()) return;
    let src = from || mimic;
    let samples = selectedSamples(src);
    if (!samples.length) {
      if (!src.targetArtist.trim()) {
        setStatus("Name an artist and Find artist lyrics, or upload / paste a sample.");
        return;
      }
      const pulled = await pullLyrics(src);
      if (!pulled) return;
      src = pulled;
      samples = selectedSamples(src);
      if (!samples.length) {
        setStatus("No lyric samples to build from.");
        return;
      }
    }
    setBusy("mimic");
    const n = selectedTracks(src).length || samples.length;
    setStatus(`Building mimic from ${n} lyric sample${n === 1 ? "" : "s"}…`);
    try {
      const res = await fetch("/api/mimic/analyze", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({
          samples,
          tracks: selectedTracks(src).map((t) => ({ title: t.title, lyrics: t.lyrics })),
          targetArtist: src.targetArtist,
          era: src.era,
        }),
      });
      const data = (await res.json()) as {
        mimic?: Partial<MusicMimic> & { suggestedDials?: Dial[] };
        error?: string;
        wallet_usd?: number;
      };
      if (!res.ok) throw new Error(data.error || "Analyze failed");
      const m = data.mimic || {};
      const suggested = (m as { suggestedDials?: Dial[] }).suggestedDials;
      const next: MusicMimic = {
        ...src,
        name: String(m.name || src.name || src.targetArtist || "Inspired-by"),
        era: String(m.era || src.era),
        systemPrompt: String(m.systemPrompt || src.systemPrompt),
        vocabularyBank: Array.isArray(m.vocabularyBank) ? m.vocabularyBank.map(String) : src.vocabularyBank,
        analysisNotes: String(m.analysisNotes || ""),
        dials: suggested?.length ? suggested : src.dials,
      };
      setMimic(next);
      setMimicShelf((shelf) => upsertMimicShelf(shelf, next));
      const path = await pushMimicToBucket(next);
      if (typeof data.wallet_usd === "number") {
        setWallet((w) => (w ? { ...w, balanceUsd: data.wallet_usd! } : w));
      }
      armMimicLane();
      setStatus(
        path
          ? `Mimic drafted and saved to your bucket · ${path}. Use this style to load dials + lyric flavor.`
          : "Mimic drafted and saved in this browser. Sign in to keep it on your Hugging Face shelf.",
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const applyMimic = () => {
    const nextWt = Math.max(mixWeights.mimic, 55);
    setMixWeight("mimic", nextWt);
    setCaptionOverride(null);
    openMix("mimic");
    setTab("simple");
    setStatus(
      `Mimic lane at ${nextWt}/100 · ${mimic.name || mimic.targetArtist || "profile"} — Frequency / Healing / Instruments unchanged`,
    );
  };

  const loadSavedMimic = (m: MusicMimic) => {
    setMimic(m);
    setPreviewTrackId(m.tracks.find((t) => t.selected)?.id || m.tracks[0]?.id || null);
    setPasteOpen(false);
    setTab("mimic");
    setStatus(`Loaded mimic · ${mimicLabel(m)}`);
  };

  const saveMimicToShelf = async () => {
    if (!mimicLoaded(mimic)) {
      setStatus("Learn or name a style before saving.");
      return;
    }
    const next = upsertMimicShelf(mimicShelf, mimic);
    if (!saveMimicShelf(next)) {
      setStatus("Could not save mimic — this browser is out of space.");
      return;
    }
    setMimicShelf(next);
    if (!isLoggedIn()) {
      setStatus(`Saved mimic locally · ${mimicLabel(mimic)}. Sign in to keep it on your Hugging Face shelf.`);
      return;
    }
    const path = await pushMimicToBucket(mimic);
    setStatus(
      path
        ? `Saved mimic · ${mimicLabel(mimic)} · ${path}`
        : `Saved mimic locally · ${mimicLabel(mimic)}. Bucket write failed.`,
    );
  };

  const importMimicFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = parseMimicJson(JSON.parse(await file.text()));
      if (!parsed || !mimicLoaded(parsed)) throw new Error("Not a MusicForge mimic file.");
      loadSavedMimic(parsed);
      const next = upsertMimicShelf(mimicShelf, parsed);
      if (saveMimicShelf(next)) setMimicShelf(next);
      if (isLoggedIn()) await pushMimicToBucket(parsed);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Could not read that mimic file.");
    }
  };

  const newMimic = () => {
    const next = emptyMimic();
    setMimic(next);
    setPreviewTrackId(null);
    setPasteOpen(false);
    setStatus("New mimic.");
  };

  const buy = async (usd: number) => {
    try {
      const url = await startCheckout(usd);
      if (url) window.location.href = url;
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
    }
  };

  const member = async () => {
    try {
      const url = await subscribeMember();
      if (url) window.location.href = url;
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
    }
  };

  const sections = parseSectionTags(lyrics);
  const previewTrack =
    mimic.tracks.find((t) => t.id === previewTrackId) ||
    mimic.tracks.find((t) => t.selected) ||
    mimic.tracks[0] ||
    null;
  const mimicBusy = busy === "mimic" || busy === "pull";

  const applyTakeSeed = (t: TakeItem) => {
    setSeedSong({ kind: "take", label: t.title, seed: t.seed, takeId: t.id });
    setSeed(t.seed);
    setRandomSeed(false);
    if (t.objectUrl) {
      setAudioUrl(t.objectUrl);
      setActiveTakeId(t.id);
    }
    if (t.lyrics) setLyrics(t.lyrics);
    if (t.caption) setCaptionOverride(t.caption);
    setStatus(`Seed from “${t.title}” · ${t.seed}`);
  };

  const onSeedFile = async (file: File | null) => {
    if (!file) return;
    try {
      const next = await seedFromAudioFile(file);
      setSeedSong(next);
      setSeed(next.seed);
      setRandomSeed(false);
      setStatus(`Seed from “${next.label}” · ${next.seed}`);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Could not read seed file");
    }
  };

  const clearSeedSong = () => {
    setSeedSong(null);
    setRandomSeed(true);
    setStatus("Seed cleared — next generate randomizes.");
  };

  return (
    <div
      className="mf-shell"
      style={
        {
          "--mf-dock-h": `${Math.min(dockH, dockMax)}px`,
          "--mf-cue-h": `${Math.min(cueH, cueMax)}px`,
          "--mf-rail-w": `${railW}px`,
        } as CSSProperties
      }
    >
      <BackgroundFx />
      <input
        ref={lyricFileRef}
        type="file"
        hidden
        multiple
        accept=".txt,.lrc,.md,.json,text/plain"
        onChange={(e) => {
          const files = e.target.files;
          e.target.value = "";
          void onLyricFiles(files);
        }}
      />
      <input
        ref={lyricFolderRef}
        type="file"
        hidden
        multiple
        {...{ webkitdirectory: "" }}
        onChange={(e) => {
          const files = e.target.files;
          e.target.value = "";
          void onLyricFiles(files);
        }}
      />
      <input
        ref={mimicFileRef}
        type="file"
        hidden
        accept=".music-mimic,.json,application/json"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          void importMimicFile(file);
        }}
      />
      <header className="mf-chrome">
        <strong className="mf-brand">MusicForge</strong>
        <div className="mf-chrome-actions">
          <button type="button" className="mf-btn primary" disabled={busy === "gen"} onClick={() => void generate()}>
            {busy === "gen" ? "Making song…" : "Make song"}
          </button>
          {busy === "gen" ? (
            <button type="button" className="mf-btn ghost" onClick={stopWork}>
              Stop
            </button>
          ) : null}
          <button
            type="button"
            className={`mf-chip${skinsOpen ? " on" : ""}`}
            onClick={() => {
              setSkinsOpen((v) => !v);
              setWalletOpen(false);
              setLoginOpen(false);
            }}
            title="Skin / Motion"
            aria-expanded={skinsOpen}
            aria-label="Skin and motion"
          >
            <PiPaletteBold /> <span className="mf-skin-label">Skin</span>
          </button>
          {user ? (
            <button
              type="button"
              className={`mf-chip${walletOpen ? " on" : ""}`}
              onClick={() => {
                setWalletOpen((v) => !v);
                setSkinsOpen(false);
              }}
            >
              <PiWalletBold /> ${((wallet?.balanceUsd ?? 0)).toFixed(2)}
            </button>
          ) : null}
          {user ? (
            <button
              type="button"
              className="mf-chip"
              onClick={() => {
                clearSession();
                setUser(null);
                setWallet(null);
                setWalletOpen(false);
              }}
            >
              <PiSignOutBold /> {user.email.split("@")[0]}
            </button>
          ) : (
            <button
              type="button"
              className={`mf-chip accent${loginOpen ? " on" : ""}`}
              onClick={openLogin}
            >
              <PiSignInBold /> Sign in
            </button>
          )}
          <button
            type="button"
            className={`mf-chip${railOpen ? " on" : ""}`}
            onClick={() => setRailOpen((v) => !v)}
            title="Library"
            aria-expanded={railOpen}
            aria-label="Library"
          >
            <PiSidebarBold />
          </button>
        </div>
      </header>

      <div className="mf-body">
        <main
          ref={mainRef}
          className={`mf-main${captionOpen && tab !== "player" ? " caption-open" : ""}${mixShown ? " mix-open" : ""}${tab === "studio" ? " lyrics-step" : ""}${tab === "player" ? " player-open" : ""}`}
        >
            <>
              <div className="mf-studio-tabs">
                <nav className="mf-steps" aria-label="How to make a song">
                  {songSteps.map((s) => (
                    <span
                      key={s.id}
                      className={`mf-step-wrap${(s.id === "mix" ? mixOpen && tab !== "mimic" && tab !== "player" : songStep === s.id) ? " on" : ""}`}
                    >
                      <button
                        type="button"
                        className={`mf-step ${s.tone}${(s.id === "mix" ? mixOpen && tab !== "mimic" && tab !== "player" : songStep === s.id) ? " on" : ""}`}
                        onClick={() => goStep(s.id)}
                      >
                        <span className="mf-step-n">{s.n}</span>
                        {s.label}
                      </button>
                      <InfoTip title={s.title} align={s.id === "mimic" || s.id === "play" ? "end" : "start"}>
                        <p>{s.help}</p>
                      </InfoTip>
                    </span>
                  ))}
                </nav>

              </div>

              {status ? (
                <p className={`mf-banner${busy ? " working" : ""}`} role="status">
                  {status}
                </p>
              ) : null}

              <div className="mf-workspace">
                <button
                  type="button"
                  className="mf-work-restore"
                  aria-label={tab === "studio" ? "Show lyrics" : tab === "mimic" ? "Show mimic" : "Show song description"}
                  onClick={() => {
                    setMixOpen(false);
                    setCaptionOpen(false);
                  }}
                >
                  <span>{tab === "studio" ? "Lyrics" : tab === "mimic" ? "Mimic" : "Song description"}</span>
                  <small>
                    {tab === "studio"
                      ? lyrics.split("\n").map((l) => l.trim()).find((l) => l && !l.startsWith("[")) || "Open lyrics"
                      : tab === "mimic"
                        ? mimic.name || mimic.targetArtist || "Open mimic"
                        : bowl.trim().split("\n")[0] || "Open description"}
                  </small>
                </button>
                {tab === "simple" ? (
                  <section className="mf-pane primary" aria-label="Mixing bowl">
                    <div className="mf-pane-head">
                      <label className="mf-label">Step 1 · Mixing bowl</label>
                      <InfoTip title="Mixing bowl">
                        <p>Describe the song in plain language. Write lyrics (cyan) turns this into verses. Then go to step 2 to edit, or step 4 to Make song.</p>
                      </InfoTip>
                    </div>
                    <textarea
                      className="mf-bowl grow"
                      value={bowl}
                      onChange={(e) => setBowl(e.target.value)}
                      placeholder="Describe the song — mood, voice, story. Then Write lyrics."
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        lyricDestRef.current = "studio";
                        void onLyricFiles(e.dataTransfer.files);
                      }}
                    />
                    <div className="mf-actions">
                      <button
                        type="button"
                        className="mf-btn write lg"
                        disabled={busy === "lyrics"}
                        onClick={() => void promptLyrics()}
                      >
                        {busy === "lyrics" ? "Writing lyrics…" : "Write lyrics"}
                      </button>
                      <InfoTip title="Write lyrics">
                        <p>Sends Mixing bowl to the house writer and fills step 2. You can still type or upload lyrics instead.</p>
                      </InfoTip>
                      <button
                        type="button"
                        className="mf-btn upload lg"
                        onClick={() => pickLyricFiles("studio")}
                      >
                        Upload lyric files
                      </button>
                      <InfoTip title="Upload lyric files">
                        <p>Drop many .txt / .lrc / .md / .json songs at once. Split a file on --- for several songs. Use Upload lyric folder for a whole directory.</p>
                      </InfoTip>
                      <button
                        type="button"
                        className="mf-btn upload"
                        onClick={() => pickLyricFiles("studio", true)}
                      >
                        Upload lyric folder
                      </button>
                      <button
                        type="button"
                        className="mf-btn primary lg"
                        disabled={busy === "gen"}
                        onClick={() => void generate()}
                      >
                        {busy === "gen" ? "Making song…" : "Make this song"}
                      </button>
                      {readyPacks.length > 1 ? (
                        <button
                          type="button"
                          className="mf-btn mix lg"
                          disabled={busy === "gen"}
                          onClick={() => void makeAllSongs()}
                        >
                          Make all {readyPacks.length} songs
                        </button>
                      ) : null}
                    </div>
                  </section>
                ) : null}

                {tab === "studio" ? (
                  <section className="mf-pane primary lyrics-split" aria-label="Lyrics studio">
                    <div className="mf-lyric-bar">
                      <div className="mf-pane-head">
                        <label className="mf-label">Step 2 · Lyrics</label>
                        <InfoTip title="Lyrics">
                          <p>This is the song Music 3 sings. Write lyrics from step 1, type here, or upload many files. [verse] / [chorus] chips mark sections. Music 3 input max is 4096 characters — extra lines are trimmed on Make song.</p>
                        </InfoTip>
                        <span
                          className={`mf-lyric-count${lyricsOverLimit ? " over" : ""}`}
                          title="Music 3 sings at most 4096 characters"
                        >
                          {lyricCharCount.toLocaleString()} / {MUSIC3_INPUT_MAX.toLocaleString()}
                        </span>
                      </div>
                      <div className="mf-actions">
                        <button
                          type="button"
                          className="mf-btn write"
                          disabled={busy === "lyrics"}
                          onClick={() => void promptLyrics()}
                        >
                          {busy === "lyrics" ? "Writing lyrics…" : "Write lyrics"}
                        </button>
                        <button
                          type="button"
                          className="mf-btn upload"
                          onClick={() => pickLyricFiles("studio")}
                        >
                          Upload files
                        </button>
                        <button
                          type="button"
                          className="mf-btn upload"
                          onClick={() => pickLyricFiles("studio", true)}
                        >
                          Upload folder
                        </button>
                        <button
                          type="button"
                          className="mf-btn primary"
                          disabled={busy === "gen"}
                          onClick={() => void generate(activePackId ? { lyrics, title: lyricPacks.find((p) => p.id === activePackId)?.title, packId: activePackId } : undefined)}
                        >
                          {busy === "gen" ? "Making song…" : "Make this song"}
                        </button>
                        {readyPacks.length > 1 ? (
                          <button
                            type="button"
                            className="mf-btn mix"
                            disabled={busy === "gen"}
                            onClick={() => void makeAllSongs()}
                          >
                            Make all {readyPacks.length}
                          </button>
                        ) : null}
                      </div>
                      <div className="mf-chips">
                        {SECTION_TAGS.map((t) => (
                          <button key={t} type="button" onClick={() => insertSection(t)}>
                            {t}
                          </button>
                        ))}
                        <InfoTip title="Section tags">
                          <p>Click a tag to insert it at the cursor. Music 3 uses [verse], [chorus], [bridge] as the arrangement.</p>
                        </InfoTip>
                      </div>
                    </div>
                    <textarea
                      ref={lyricsRef}
                      className="mf-bowl lyrics grow"
                      value={lyrics}
                      onChange={(e) => setLyrics(e.target.value)}
                      placeholder="Lyrics with [verse] and [chorus] tags. Upload many files to fill the list below."
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        lyricDestRef.current = "studio";
                        void onLyricFiles(e.dataTransfer.files);
                      }}
                    />
                    {lyricPacks.length ? (
                      <div className="mf-lyric-queue" aria-label="Uploaded lyrics">
                        <div className="mf-lyric-queue-head">
                          <span>
                            {lyricPacks.length} uploaded song{lyricPacks.length === 1 ? "" : "s"}
                          </span>
                          <button
                            type="button"
                            className="mf-btn ghost"
                            onClick={() => {
                              setLyricPacks([]);
                              setActivePackId(null);
                            }}
                          >
                            Clear list
                          </button>
                        </div>
                        <ul>
                          {lyricPacks.map((p) => (
                            <li key={p.id} className={activePackId === p.id ? "on" : ""}>
                              <button type="button" className="mf-pack-load" onClick={() => loadPack(p)}>
                                {p.title}
                              </button>
                              <small>
                                {p.status === "making"
                                  ? "making"
                                  : p.status === "done"
                                    ? "done"
                                    : p.status === "error"
                                      ? "retry"
                                      : "ready"}
                              </small>
                              <button
                                type="button"
                                className="mf-x"
                                aria-label={`Remove ${p.title}`}
                                onClick={() => {
                                  setLyricPacks((cur) => cur.filter((x) => x.id !== p.id));
                                  if (activePackId === p.id) setActivePackId(null);
                                }}
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {sections.length ? (
                      <>
                        <SplitHandle
                          label="Resize performance cues"
                          value={cueH}
                          min={CUE_MIN}
                          max={cueMax}
                          onChange={setCueH}
                          onReset={() => setCueH(144)}
                        />
                        <div className="mf-cues">
                          {sections.map((tag) => (
                            <div key={tag} className="mf-section-cues">
                              <span>{tag}</span>
                              {PERFORMANCE_CHIPS.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  className={cues.find((x) => x.tag === tag)?.cue === c ? "on" : ""}
                                  onClick={() => toggleCue(tag, c)}
                                >
                                  {c}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </section>
                ) : null}

                <section
                  className="mf-pane primary player"
                  aria-label="Player"
                  hidden={tab !== "player"}
                  aria-hidden={tab !== "player"}
                >
                  <Player
                    src={audioUrl}
                    status={status}
                    generating={busy === "gen"}
                    finishing={busy === "finish" || busy === "karaoke"}
                    reworking={busy === "rework"}
                    finishEnabled={Boolean(wallet?.productionFinish)}
                    open={tab === "player"}
                    playNonce={playNonce}
                    takes={takes}
                    activeId={activeTakeId}
                    onGenerate={(opts) => void generate(opts)}
                    onStop={stopWork}
                    onFinish={(take, preset) => void finishTake(take, preset)}
                    onRework={(take, nextLyrics) => void reworkTake(take, nextLyrics)}
                    onListen={listenTake}
                    onPlayTake={playTake}
                    onSeedTake={applyTakeSeed}
                    onLoadTake={loadTake}
                    onDeleteTakes={deleteTakes}
                    onRenameTake={(t, title) => setTakes((cur) => renameTake(cur, t.id, title))}
                    onMoveTakes={(ids, folderId) => setTakes((cur) => moveTakes(cur, ids, folderId))}
                    onImportFiles={(files, folderId) => void importFiles(files, folderId)}
                    onKaraokeBed={(take) => void stripKaraokeBed(take)}
                    onSyncLyrics={(take) => void syncKaraokeLyrics(take)}
                    studioLyrics={lyrics}
                  />
                </section>

                {tab === "mimic" ? (
                  <section className="mf-pane primary mimic" aria-label="Mimic">
                    <div className="mf-pane-head">
                      <p className="mf-hint">
                        Inspired-by style — not a voice clone. Step 1 find or upload lyrics · 2 pick up to 10 · 3 Learn this style · 4 Use this style.
                      </p>
                      <InfoTip title="Mimic" align="end">
                        <p>Find artist lyrics from public catalogs, or upload a pile of your own .txt files. Check the ones to learn from, then Learn this style. Use this style writes the mix + lyric flavor.</p>
                      </InfoTip>
                    </div>
                    <form
                      className="mf-mimic-search"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void pullLyrics();
                      }}
                    >
                      <input
                        className="mf-input"
                        placeholder="Artist"
                        aria-label="Artist"
                        value={mimic.targetArtist}
                        onChange={(e) => setMimic({ ...mimic, targetArtist: e.target.value })}
                      />
                      <input
                        className="mf-input era"
                        placeholder="Era (optional)"
                        aria-label="Era"
                        value={mimic.era}
                        onChange={(e) => setMimic({ ...mimic, era: e.target.value })}
                      />
                      <button type="submit" className="mf-btn write lg" disabled={mimicBusy}>
                        {busy === "pull" ? "Finding lyrics…" : "Find artist lyrics"}
                      </button>
                      <button
                        type="button"
                        className="mf-btn upload lg"
                        onClick={() => pickLyricFiles("mimic")}
                      >
                        Upload lyric files
                      </button>
                      <button
                        type="button"
                        className="mf-btn upload"
                        onClick={() => pickLyricFiles("mimic", true)}
                      >
                        Upload lyric folder
                      </button>
                      <button
                        type="button"
                        className="mf-btn style lg"
                        disabled={mimicBusy}
                        onClick={() => void analyzeMimic()}
                      >
                        {busy === "mimic" ? "Learning…" : "Learn this style"}
                      </button>
                    </form>

                    {mimicShelf.length ? (
                      <div className="mf-lyric-queue" aria-label="Saved mimics">
                        <div className="mf-lyric-queue-head">
                          <span>
                            {mimicShelf.length} saved mimic{mimicShelf.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        <ul>
                          {mimicShelf.map((m) => (
                            <li key={m.id} className={m.id === mimic.id ? "on" : ""}>
                              <button type="button" className="mf-pack-load" onClick={() => loadSavedMimic(m)}>
                                {mimicLabel(m)}
                              </button>
                              <small>{m.era || (m.tracks.length ? `${m.tracks.length} tracks` : "style")}</small>
                              <button
                                type="button"
                                className="mf-x"
                                aria-label={`Remove ${mimicLabel(m)}`}
                                onClick={() => {
                                  setMimicShelf((cur) => cur.filter((x) => x.id !== m.id));
                                  if (isLoggedIn()) void deleteRemoteMimic(m.id).catch((e) => setStatus(e instanceof Error ? e.message : String(e)));
                                }}
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {mimic.tracks.length ? (
                      <div className="mf-mimic-body">
                        <ul className="mf-track-list" aria-label="Pulled lyric tracks">
                          {mimic.tracks.map((t) => (
                            <li key={t.id} className={`mf-track${previewTrack?.id === t.id ? " on" : ""}`}>
                              <input
                                type="checkbox"
                                checked={t.selected}
                                aria-label={`Use ${t.title} in mimic`}
                                onChange={(e) => setMimic(setTrackSelected(mimic, t.id, e.target.checked))}
                              />
                              <button type="button" className="mf-track-main" onClick={() => setPreviewTrackId(t.id)}>
                                {t.title}
                                <small>
                                  {t.album || t.artist}
                                  {t.year ? ` · ${t.year}` : ""}
                                </small>
                              </button>
                            </li>
                          ))}
                        </ul>
                        <div className="mf-lyric-preview">
                          <label className="mf-label">
                            {previewTrack ? previewTrack.title : "Lyric sample"}
                          </label>
                          <textarea
                            className="mf-bowl grow"
                            placeholder="Select a track to read lyrics"
                            value={previewTrack?.lyrics || ""}
                            onChange={(e) => {
                              if (!previewTrack) return;
                              setMimic(setTrackLyrics(mimic, previewTrack.id, e.target.value));
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mf-mimic-paste">
                        <textarea
                          className="mf-bowl grow"
                          placeholder="Or paste lyric sample 1"
                          value={mimic.sampleTexts[0] || ""}
                          onChange={(e) =>
                            setMimic({ ...mimic, sampleTexts: [e.target.value, mimic.sampleTexts[1] || ""] })
                          }
                        />
                        <textarea
                          className="mf-bowl grow"
                          placeholder="Lyric sample 2 (optional)"
                          value={mimic.sampleTexts[1] || ""}
                          onChange={(e) =>
                            setMimic({ ...mimic, sampleTexts: [mimic.sampleTexts[0] || "", e.target.value] })
                          }
                        />
                      </div>
                    )}

                    {mimic.tracks.length ? (
                      <button
                        type="button"
                        className="mf-btn ghost mf-paste-toggle"
                        onClick={() => setPasteOpen((v) => !v)}
                      >
                        {pasteOpen ? "Hide pasted samples" : "Paste samples instead"}
                      </button>
                    ) : null}
                    {pasteOpen && mimic.tracks.length ? (
                      <div className="mf-mimic-paste compact">
                        <textarea
                          className="mf-bowl"
                          placeholder="Paste lyric sample 1"
                          value={mimic.sampleTexts[0] || ""}
                          onChange={(e) =>
                            setMimic({ ...mimic, sampleTexts: [e.target.value, mimic.sampleTexts[1] || ""] })
                          }
                        />
                        <textarea
                          className="mf-bowl"
                          placeholder="Lyric sample 2 (optional)"
                          value={mimic.sampleTexts[1] || ""}
                          onChange={(e) =>
                            setMimic({ ...mimic, sampleTexts: [mimic.sampleTexts[0] || "", e.target.value] })
                          }
                        />
                      </div>
                    ) : null}

                    <div className="mf-row">
                      <span className="mf-hint">
                        {mimic.tracks.length
                          ? `${selectedTracks(mimic).length} of ${mimic.tracks.length} selected`
                          : "Paste or upload samples if Find artist lyrics comes up empty"}
                      </span>
                      <button type="button" className="mf-btn style lg" onClick={applyMimic}>
                        Use this style
                      </button>
                      <button
                        type="button"
                        className="mf-btn"
                        disabled={!mimicLoaded(mimic)}
                        title="Keep this mimic on your Hugging Face shelf"
                        onClick={() => void saveMimicToShelf()}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="mf-btn ghost"
                        onClick={() => mimicFileRef.current?.click()}
                      >
                        Import
                      </button>
                      <button
                        type="button"
                        className="mf-btn ghost"
                        onClick={() => {
                          const blob = new Blob([serializeMimic(mimic)], { type: "application/json" });
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(blob);
                          a.download = `${(mimic.name || mimic.targetArtist || "music").replace(/\s+/g, "_")}.music-mimic`;
                          a.click();
                        }}
                      >
                        Export
                      </button>
                      <button type="button" className="mf-btn ghost" onClick={newMimic}>
                        New
                      </button>
                    </div>
                    {mimic.vocabularyBank.length ? (
                      <div className="mf-chips vocab" aria-label="Vocabulary">
                        {mimic.vocabularyBank.slice(0, 16).map((w) => (
                          <span key={w}>{w}</span>
                        ))}
                      </div>
                    ) : null}
                    {mimic.analysisNotes ? <p className="mf-hint notes">{mimic.analysisNotes}</p> : null}
                  </section>
                ) : null}

              </div>

              {tab !== "player" ? (
                <SplitHandle
                  className="mf-dock-split"
                  label="Resize lyrics and mix"
                  value={dockH}
                  min={DOCK_MIN}
                  max={dockMax}
                  getMax={() =>
                    Math.max(DOCK_MIN, Math.round((mainRef.current?.clientHeight ?? 0) * 0.72))
                  }
                  onChange={(h) => {
                    setDockH(h);
                    if (h >= 120) setMixOpen(true);
                  }}
                  onReset={() => setMixOpen(false)}
                />
              ) : null}

              {tab !== "player" ? (
              <div
                className={`mf-dock${mixShown ? " open" : ""}${captionOpen ? " caption-open" : ""}`}
              >
              {tab !== "mimic" ? (
                <>
                  <div className="mf-auto-mix">
                    <span className="mf-auto-mix-label">
                      Auto sliders
                      <InfoTip title="Auto sliders">
                        <p>
                          Check a box to fill that lane from Mixing bowl. Instruments, Frequencies, and
                          Weights write the matching sliders. Lyrics turns the vocal lane on and opens
                          the words. Uncheck zeros that lane. Tabs under this row are the same sliders
                          for hand tweaks.
                        </p>
                      </InfoTip>
                      <span className="mf-auto-mix-recipe">{pickRecipe(bowl, genres).label}</span>
                    </span>
                    <div className="mf-auto-boxes" role="group" aria-label="Auto sliders">
                      {AUTO_BOXES.map((b) => (
                        <label
                          key={b.id}
                          className={`mf-auto-box${autoBoxes[b.id] ? " on" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={autoBoxes[b.id]}
                            onChange={() => toggleAutoBox(b.id)}
                          />
                          {b.label}
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="mf-btn write"
                      disabled={busy === "lyrics" || busy === "gen"}
                      onClick={() => {
                        void promptLyrics(10).then((ok) => {
                          if (ok) void generate();
                        });
                      }}
                    >
                      {busy === "lyrics" ? "Picking 10…" : busy === "gen" ? "Making song…" : "Pick 10"}
                    </button>
                    <InfoTip title="Pick 10">
                      <p>
                        Writes ten lyric sheets from Mixing bowl, Jev picks one, then Make song on mill.
                        Same wallet as ten Write lyrics plus one Make song.
                      </p>
                    </InfoTip>
                  </div>
                  <div className="mf-mix-head">
                    <div className="mf-mix-tabs" role="tablist" aria-label="Mix banks">
                      {MIX_BANKS.map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          role="tab"
                          aria-selected={mixOpen && mixBank === b.id}
                          aria-expanded={mixOpen && mixBank === b.id}
                          className={mixOpen && mixBank === b.id ? "on" : ""}
                          onClick={() => {
                            if (mixOpen && mixBank === b.id) setMixOpen(false);
                            else openMix(b.id);
                          }}
                        >
                          {b.label}
                        </button>
                      ))}
                      {[...customInstrumentDials(dials), ...customVocalDials(dials)].map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          role="tab"
                          aria-selected={mixOpen && mixBank === `ch:${d.id}`}
                          className={mixOpen && mixBank === `ch:${d.id}` ? "on" : ""}
                          onClick={() => {
                            const id = `ch:${d.id}` as const;
                            if (mixOpen && mixBank === id) setMixOpen(false);
                            else {
                              setEqTarget(d.id);
                              openMix(id);
                            }
                          }}
                        >
                          <span className="mf-inst-ico" aria-hidden>
                            {instrumentGlyph(d.label)}
                          </span>
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <label className="mf-dur">
                      {duration}s
                      <input
                        type="range"
                        min={8}
                        max={300}
                        value={duration}
                        aria-label="Duration"
                        onChange={(e) => setDuration(Number(e.target.value))}
                      />
                    </label>
                    <div className="mf-add-dial">
                      <input
                        className="mf-input"
                        placeholder="Bagpipes, didgeridoo…"
                        aria-label="New instrument or vocal dial"
                        value={newDial}
                        onChange={(e) => setNewDial(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addDial()}
                      />
                      <button type="button" className="mf-btn" onClick={() => addDial()}>
                        ＋ Add dial
                      </button>
                    </div>
                  </div>
                  <div className="mf-mix-pane" role="tabpanel" hidden={!mixOpen} aria-hidden={!mixOpen}>
                    {mixBank === "frequency" ? (
                      <>
                        <LaneWeightRow
                          label="Frequency weight"
                          value={mixWeights.frequency}
                          onChange={(n) => setMixWeight("frequency", n)}
                          tip="How hard the EQ curve pulls the mix. 0 = ignore this EQ. Mimic and Healing keep their own sliders."
                        />
                        <p className="mf-hint">EQ the whole mix, vocals, a style tone, or one instrument. Curves write into Advanced.</p>
                        <div className="mf-chips mf-eq-target" role="group" aria-label="EQ target">
                          <span className="mf-label">Apply to</span>
                          <button
                            type="button"
                            className={eqTarget === "all" ? "on" : ""}
                            onClick={() => setEqTarget("all")}
                          >
                            Entire track
                          </button>
                          <button
                            type="button"
                            className={eqTarget === "vocals" ? "on" : ""}
                            onClick={() => setEqTarget("vocals")}
                          >
                            Vocals
                          </button>
                          <button
                            type="button"
                            className={eqTarget === "style" ? "on" : ""}
                            onClick={() => setEqTarget("style")}
                          >
                            Style
                          </button>
                          {[...customInstrumentDials(dials), ...customVocalDials(dials)].map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              className={eqTarget === d.id ? "on" : ""}
                              onClick={() => setEqTarget(d.id)}
                            >
                              <span className="mf-inst-ico" aria-hidden>
                                {instrumentGlyph(d.label)}
                              </span>
                              {d.label}
                            </button>
                          ))}
                          <label className="mf-eq-select-wrap">
                            <span className="mf-sr">Stock instrument</span>
                            <select
                              className="mf-eq-select"
                              aria-label="EQ a stock instrument"
                              value={
                                instrumentDials(dials).some((d) => !d.custom && d.id === eqTarget)
                                  ? eqTarget
                                  : ""
                              }
                              onChange={(e) => {
                                const id = e.target.value;
                                if (id) setEqTarget(id);
                              }}
                            >
                              <option value="">Stock instruments…</option>
                              {instrumentDials(dials)
                                .filter((d) => !d.custom)
                                .map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {instrumentGlyph(d.label)} {d.label}
                                  </option>
                                ))}
                            </select>
                          </label>
                        </div>
                        <div className="mf-heal">
                          <LaneWeightRow
                            label="Healing weight"
                            value={mixWeights.healing}
                            onChange={(n) => setMixWeight("healing", n)}
                            tip="How much 432/528-style drones color the piece. 0 = off. Does not wipe Frequency EQ or Mimic."
                          />
                          <div className="mf-pane-head">
                            <span className="mf-label">Healing frequencies</span>
                            <InfoTip title="Healing frequencies">
                              <p>
                                Sound-healing tones used as drones, bowl partials, or concert pitch. Pick Hz chips and/or a
                                preset. That writes EQ, instruments, Mixing bowl, lyrics, and the Advanced caption. Not a
                                medical claim — it is a production category for MiniMax Music 3.
                              </p>
                            </InfoTip>
                          </div>
                          <div className="mf-chips mf-heal-tones" role="group" aria-label="Healing Hz">
                            {HEALING_TONES.map((t) => (
                              <button
                                key={t.hz}
                                type="button"
                                className={healingTones.includes(t.hz) ? "on" : ""}
                                title={t.blurb}
                                onClick={() => toggleHealingTone(t.hz)}
                              >
                                {t.hz} Hz
                              </button>
                            ))}
                          </div>
                          <div className="mf-chips mf-heal-presets" role="group" aria-label="Healing presets">
                            {HEALING_PRESETS.map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                className={healingPresetId === p.id ? "on" : ""}
                                onClick={() => applyHealingPreset(p.id)}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                          {healingTones.length || isHealingStyle(genres, healingTones) ? (
                            <p className="mf-hint">
                              {healingTones.length
                                ? healingTones
                                    .map((hz) => {
                                      const t = toneByHz(hz);
                                      return t ? `${t.hz} Hz · ${t.name} — ${t.blurb}` : `${hz} Hz`;
                                    })
                                    .join(" ")
                                : "Healing genre on — add a Hz chip or a preset so the caption names a tone."}
                            </p>
                          ) : (
                            <p className="mf-hint">
                              Presets fill Mixing bowl, lyrics, slow BPM, bowls/drones, and a healing caption. Or tap Hz chips
                              alone.
                            </p>
                          )}
                        </div>
                        {eqTarget === "all" ? (
                          <div className="mf-bpm" role="group" aria-label="BPM">
                            <span className="mf-label">BPM {bpm}</span>
                            {BPM_PRESETS.map((n) => (
                              <button
                                key={n}
                                type="button"
                                className={bpm === n ? "on" : ""}
                                onClick={() => {
                                  setBpm(n);
                                  setCaptionOverride(null);
                                }}
                              >
                                {n}
                              </button>
                            ))}
                            <input
                              className="mf-bpm-num"
                              type="number"
                              min={40}
                              max={220}
                              value={bpm}
                              aria-label="BPM"
                              onChange={(e) => {
                                setBpm(Math.max(40, Math.min(220, Number(e.target.value) || 40)));
                                setCaptionOverride(null);
                              }}
                            />
                          </div>
                        ) : null}
                        {eqTarget === "style" ? (
                          <p className="mf-hint">Tonal color of the production style — not a single instrument.</p>
                        ) : null}
                        {eqTarget === "vocals" ? (
                          <p className="mf-hint">Lead vocal equalizer — voice only, stacked on the track curve.</p>
                        ) : null}
                        {eqTarget !== "all" && eqTarget !== "style" && eqTarget !== "vocals"
                          ? (() => {
                              const inst = dials.find((d) => d.id === eqTarget);
                              if (!inst) return null;
                              return (
                                <div className="mf-channel-head">
                                  <div className="mf-dial-row">
                                    <span className="mf-dial-lab">
                                      <span className="mf-inst-ico" aria-hidden>
                                        {instrumentGlyph(inst.label)}
                                      </span>
                                      {inst.label} level
                                    </span>
                                    <input
                                      type="range"
                                      min={0}
                                      max={100}
                                      value={inst.value}
                                      aria-label={`${inst.label} level`}
                                      onChange={(e) => setDial(inst.id, Number(e.target.value))}
                                    />
                                    <b>{inst.value}</b>
                                    <span />
                                    {inst.custom ? (
                                      <button
                                        type="button"
                                        className="mf-x"
                                        onClick={() => removeDial(inst.id)}
                                        aria-label={`Remove ${inst.label}`}
                                      >
                                        ✕
                                      </button>
                                    ) : (
                                      <span />
                                    )}
                                  </div>
                                  <p className="mf-hint">
                                    {inst.bank === "vocals" ? vocalLayerVoice(inst.label) : instrumentVoice(inst.label)}
                                  </p>
                                  <div className="mf-chips">
                                    <button type="button" className="mf-btn" onClick={() => makeLead(inst.id)}>
                                      Make lead
                                    </button>
                                  </div>
                                </div>
                              );
                            })()
                          : null}
                        <EqStrip
                          label={
                            eqTarget === "all"
                              ? "Track equalizer"
                              : eqTarget === "style"
                                ? "Style equalizer"
                                : eqTarget === "vocals"
                                  ? "Lead vocal equalizer"
                                  : `${dials.find((d) => d.id === eqTarget)?.label || "Instrument"} equalizer`
                          }
                          filters={eqFiltersFor(eqTarget)}
                          onChange={(next) => writeEq(eqTarget, next)}
                        />
                      </>
                    ) : null}
                    {mixBank === "instruments" ? (
                      <>
                        <LaneWeightRow
                          label="Instruments weight"
                          value={mixWeights.instruments}
                          onChange={(n) => setMixWeight("instruments", n)}
                          tip="How loud named instruments speak versus the rest of the mix. 0 = ignore this lane."
                        />
                        <p className="mf-hint">
                          Named instruments are featured in the caption. EQ each one, or Make lead to duck the bed.
                        </p>
                        <div className="mf-chips" aria-label="Quick instruments">
                          {QUICK_INSTRUMENTS.map((name) => (
                            <button
                              key={name}
                              type="button"
                              className={
                                dials.some((d) => d.bank === "instruments" && sameInstrument(d.label, name))
                                  ? "on"
                                  : ""
                              }
                              onClick={() => addDial(name)}
                            >
                              <span className="mf-inst-ico" aria-hidden>
                                {instrumentGlyph(name)}
                              </span>
                              {name}
                            </button>
                          ))}
                        </div>
                        <div className="mf-chips" aria-label="Healing instruments">
                          <span className="mf-label">Healing</span>
                          {HEALING_INSTRUMENTS.map((name) => (
                            <button
                              key={name}
                              type="button"
                              className={
                                dials.some((d) => d.bank === "instruments" && sameInstrument(d.label, name))
                                  ? "on"
                                  : ""
                              }
                              onClick={() => addDial(name)}
                            >
                              <span className="mf-inst-ico" aria-hidden>
                                {instrumentGlyph(name)}
                              </span>
                              {name}
                            </button>
                          ))}
                        </div>
                        <section className="mf-dials" aria-label="Instruments">
                          {dials.filter((d) => d.custom).map(dialRow)}
                          {dials.filter((d) => !d.custom && d.bank === "instruments").map(dialRow)}
                        </section>
                      </>
                    ) : null}
                    {mixBank === "vocals" ? (
                      <>
                        <LaneWeightRow
                          label="Vocals weight"
                          value={mixWeights.vocals}
                          onChange={(n) => setMixWeight("vocals", n)}
                          tip="How much vocal styling (delivery, EQ, layers) applies. 0 = treat as instrumental."
                        />
                        <p className="mf-hint">
                          Press a vocal into the mix. Extra parts compile as mandatory vocal layers.
                        </p>
                        <div className="mf-chips" role="group" aria-label="Vocal layers">
                          <span className="mf-label">Layers</span>
                          {QUICK_VOCALS.map((name) => (
                            <button
                              key={name}
                              type="button"
                              className={
                                dials.some((d) => d.bank === "vocals" && d.custom && sameVocalLayer(d.label, name))
                                  ? "on"
                                  : ""
                              }
                              onClick={() => toggleVocalLayer(name)}
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                        {customVocalDials(dials).length ? (
                          <section className="mf-dials" aria-label="Vocal layers in mix">
                            {customVocalDials(dials).map(dialRow)}
                          </section>
                        ) : null}
                        <div className="mf-chips" role="group" aria-label="Vocal delivery">
                          <span className="mf-label">Delivery</span>
                          {VOCAL_DELIVERY.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              className={vocal.delivery === d.id ? "on" : ""}
                              onClick={() => patchVocal({ delivery: d.id })}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                        <div className="mf-chips" role="group" aria-label="Vocal gender">
                          <span className="mf-label">Voice</span>
                          {VOCAL_GENDER.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              className={vocal.gender === d.id ? "on" : ""}
                              onClick={() =>
                                patchVocal({ gender: (vocal.gender === d.id ? "" : d.id) as VocalGender })
                              }
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                        <div className="mf-chips" role="group" aria-label="Vocal register">
                          <span className="mf-label">Register</span>
                          {VOCAL_REGISTER.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              className={vocal.register === d.id ? "on" : ""}
                              onClick={() =>
                                patchVocal({
                                  register: (vocal.register === d.id ? "" : d.id) as VocalRegister,
                                })
                              }
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                        <div className="mf-chips" role="group" aria-label="Mic">
                          <span className="mf-label">Mic</span>
                          {VOCAL_MIC.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              className={vocal.mic === d.id ? "on" : ""}
                              onClick={() =>
                                patchVocal({ mic: (vocal.mic === d.id ? "" : d.id) as VocalMic })
                              }
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                        <section className="mf-dials" aria-label="Vocal mix">
                          {dials.filter((d) => d.bank === "vocals" && d.id !== "vocaleq").map(dialRow)}
                        </section>
                        <div className="mf-chips">
                          <button
                            type="button"
                            className="mf-btn"
                            onClick={() => {
                              setEqTarget("vocals");
                              openMix("frequency");
                            }}
                          >
                            Vocal EQ
                          </button>
                        </div>
                        <EqStrip
                          label="Lead vocal equalizer"
                          filters={eqFiltersFor("vocals")}
                          onChange={(next) => writeEq("vocals", next)}
                        />
                      </>
                    ) : null}
                    {mixBank === "style" ? (
                      <>
                        <LaneWeightRow
                          label="Style weight"
                          value={mixWeights.style}
                          onChange={(n) => setMixWeight("style", n)}
                          tip="How much genre / energy / space tints the mix. 0 = ignore Style dials."
                        />
                        <p className="mf-hint">
                          Genre and feel. Apply a style-wide tone curve from Frequency → Style.
                        </p>
                        <div className="mf-chips">
                          <button
                            type="button"
                            className="mf-btn"
                            onClick={() => {
                              setEqTarget("style");
                              openMix("frequency");
                            }}
                          >
                            Style EQ
                          </button>
                        </div>
                        <div className="mf-chips" aria-label="Genres">
                          {GENRE_CHIPS.map((g) => (
                            <button
                              key={g}
                              type="button"
                              className={genres.includes(g) ? "on" : ""}
                              onClick={() => {
                                setCaptionOverride(null);
                                setGenres((gs) =>
                                  gs.includes(g) ? gs.filter((x) => x !== g) : [...gs, g],
                                );
                              }}
                            >
                              {g}
                            </button>
                          ))}
                        </div>
                        <section className="mf-dials" aria-label="Style">
                          {dials.filter((d) => !d.custom && d.bank === "style").map(dialRow)}
                        </section>
                      </>
                    ) : null}
                    {mixBank === "mimic" ? (
                      <section className="mf-dials" aria-label="Mimic">
                        <LaneWeightRow
                          label="Mimic weight"
                          value={mixWeights.mimic}
                          onChange={(n) => setMixWeight("mimic", n)}
                          tip="How much the inspired-by profile pulls the mix. 0 = Mimic off. Does not overwrite Frequency or Healing."
                        />
                        <p className="mf-hint">
                          Inspired-by {mimic.name || mimic.targetArtist || "none"} — not a voice clone.
                        </p>
                        <input
                          className="mf-input"
                          placeholder="Inspired-by name"
                          aria-label="Inspired-by name"
                          value={mimic.name || mimic.targetArtist}
                          onChange={(e) => {
                            setCaptionOverride(null);
                            setMimic({ ...mimic, name: e.target.value, targetArtist: e.target.value });
                          }}
                        />
                        <button type="button" className="mf-btn upload" onClick={() => setTab("mimic")}>
                          Find artist lyrics
                        </button>
                      </section>
                    ) : null}
                    {mixBank === "seed" ? (
                      <>
                        <LaneWeightRow
                          label="Seed weight"
                          value={mixWeights.seed}
                          onChange={(n) => setMixWeight("seed", n)}
                          tip="How strongly to follow a seed song or locked seed. 0 = seed is RNG only, not a style takeover."
                        />
                      <div className="mf-meta-row">
                        <label className="mf-dur">
                          Duration {duration}s
                          <input
                            type="range"
                            min={8}
                            max={300}
                            value={duration}
                            onChange={(e) => setDuration(Number(e.target.value))}
                          />
                        </label>
                        <label>
                          Seed
                          <input
                            type="number"
                            value={seed}
                            onChange={(e) => {
                              setSeed(Number(e.target.value));
                              setRandomSeed(false);
                              setCaptionOverride(null);
                            }}
                          />
                        </label>
                        <label className="mf-check">
                          <input
                            type="checkbox"
                            checked={randomSeed}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setRandomSeed(on);
                              if (on) setSeedSong(null);
                              setCaptionOverride(null);
                            }}
                          />
                          Randomize
                        </label>
                        <input
                          ref={seedFileRef}
                          type="file"
                          accept="audio/*,.wav,.mp3,.flac,.ogg,.m4a"
                          hidden
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            e.target.value = "";
                            void onSeedFile(f);
                          }}
                        />
                        <button type="button" className="mf-btn" onClick={() => seedFileRef.current?.click()}>
                          Add seed
                        </button>
                        {seedSong ? (
                          <span className="mf-seed-chip">
                            {seedSong.label} · {seedSong.seed}
                            <button type="button" className="mf-x" onClick={clearSeedSong} aria-label="Clear seed">
                              ✕
                            </button>
                          </span>
                        ) : null}
                      </div>
                      </>
                    ) : null}
                    {mixBank.startsWith("ch:")
                      ? (() => {
                          const inst = dials.find((d) => d.id === mixBank.slice(3));
                          if (!inst) {
                            return <p className="mf-hint">That instrument was removed.</p>;
                          }
                          return (
                            <div className="mf-channel">
                              <LaneWeightRow
                                label="Instruments weight"
                                value={mixWeights.instruments}
                                onChange={(n) => setMixWeight("instruments", n)}
                                tip="This channel lives in the Instruments lane. Weight 0 = ignore named instruments."
                              />
                              <p className="mf-hint">{instrumentVoice(inst.label)}</p>
                              <div className="mf-dial-row">
                                <span className="mf-dial-lab">
                                  <span className="mf-inst-ico" aria-hidden>
                                    {instrumentGlyph(inst.label)}
                                  </span>
                                  {inst.label} level
                                </span>
                                <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  value={inst.value}
                                  aria-label={`${inst.label} level`}
                                  onChange={(e) => setDial(inst.id, Number(e.target.value))}
                                />
                                <b>{inst.value}</b>
                                <span />
                                <button
                                  type="button"
                                  className="mf-x"
                                  onClick={() => removeDial(inst.id)}
                                  aria-label={`Remove ${inst.label}`}
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="mf-chips">
                                <button type="button" className="mf-btn" onClick={() => makeLead(inst.id)}>
                                  Make lead
                                </button>
                                <button type="button" className="mf-btn" onClick={() => jumpEq(inst.id)}>
                                  Open in Frequency
                                </button>
                              </div>
                              <EqStrip
                                label={`${inst.label} equalizer`}
                                filters={inst.filters ?? flatFilters()}
                                onChange={(next) => writeEq(inst.id, next)}
                              />
                            </div>
                          );
                        })()
                      : null}
                  </div>
                </>
              ) : null}

              <div className={`mf-advanced${captionOpen ? " open" : ""}`}>
                <button
                  type="button"
                  className="mf-advanced-toggle"
                  aria-expanded={captionOpen}
                  onClick={() => {
                    setCaptionOpen((v) => {
                      const next = !v;
                      if (next && !phoneLayout()) setDockH((h) => Math.max(h, 260));
                      return next;
                    });
                  }}
                >
                  Advanced · compiled caption
                </button>
                {captionOpen ? (
                  <div className="mf-advanced-body">
                    <textarea
                      className="mf-bowl"
                      value={captionText}
                      onChange={(e) => setCaptionOverride(e.target.value)}
                      spellCheck={false}
                      aria-label="Compiled caption"
                    />
                  </div>
                ) : null}
              </div>
              </div>
              ) : null}
            </>
        </main>

        {railOpen ? (
          <button
            type="button"
            className="mf-backdrop rail"
            aria-label="Close library"
            onClick={closeOverlays}
          />
        ) : null}

        {walletOpen && user ? (
          <button
            type="button"
            className="mf-backdrop wallet"
            aria-label="Close wallet"
            onClick={() => setWalletOpen(false)}
          />
        ) : null}

        {loginOpen && !user ? (
          <button
            type="button"
            className="mf-backdrop wallet"
            aria-label="Close sign in"
            onClick={() => setLoginOpen(false)}
          />
        ) : null}

        {skinsOpen ? (
          <button
            type="button"
            className="mf-backdrop wallet"
            aria-label="Close skins"
            onClick={() => setSkinsOpen(false)}
          />
        ) : null}

        {skinsOpen ? (
          <div className="mf-skins-pop" role="dialog" aria-label="Skin and motion">
            <div className="mf-wallet-head">
              <span>Skin / Motion</span>
              <button type="button" className="mf-x" onClick={() => setSkinsOpen(false)} aria-label="Close skins">
                ✕
              </button>
            </div>
            <p className="mf-hint">Neon catalog</p>
            <div className="mf-skin-row">
              {pickerSkins.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`mf-skin-chip${themeId === t.id ? " on" : ""}`}
                  title={t.description}
                  onClick={() => {
                    selectTheme(t.id);
                    setThemeId(t.id as ThemeId);
                    applyUiPrefs(uiPrefs);
                  }}
                >
                  <span className="mf-skin-swatches" aria-hidden>
                    {t.preview.map((c, i) => (
                      <span key={i} style={{ background: c }} />
                    ))}
                  </span>
                  {t.name}
                </button>
              ))}
            </div>
            <p className="mf-hint">Type</p>
            <p className="mf-hint">Font color</p>
            <div className="mf-skin-row" role="group" aria-label="Font color">
              {TEXT_COLORS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`mf-skin-chip${uiPrefs.textColor === c.id ? " on" : ""}`}
                  style={{ color: c.text }}
                  onClick={() => {
                    const next = { ...uiPrefs, textColor: c.id };
                    setUiPrefs(next);
                    saveUiPrefs(next);
                    applyUiPrefs(next);
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <p className="mf-hint">Font size</p>
            <div className="mf-skin-row" role="group" aria-label="Font size">
              {TEXT_SIZES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`mf-skin-chip${uiPrefs.textSize === s.id ? " on" : ""}`}
                  onClick={() => {
                    const next = { ...uiPrefs, textSize: s.id };
                    setUiPrefs(next);
                    saveUiPrefs(next);
                    applyUiPrefs(next);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mf-hint">Motion</p>
            <div className="mf-skin-row">
              {pickerMotions.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`mf-skin-chip${fxId === o.id ? " on" : ""}`}
                  title={o.blurb}
                  onClick={() => {
                    setBgFxId(o.id);
                    setFxId(o.id);
                  }}
                >
                  {o.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {loginOpen && !user ? (
          <div className="mf-wallet-pop mf-login-pop" role="dialog" aria-label="VividLex sign in">
            <div className="mf-wallet-head">
              <span>VividLex sign in</span>
              <button type="button" className="mf-x" onClick={() => setLoginOpen(false)} aria-label="Close sign in">
                ✕
              </button>
            </div>
            <p className="mf-hint">Same email and password as vividlex.com — use this for bots and extra people, not your Google account.</p>
            <form
              className="mf-login-fields"
              onSubmit={(e) => {
                e.preventDefault();
                void onEmailLogin();
              }}
            >
              <label>
                Email
                <input
                  className="mf-input"
                  type="email"
                  autoComplete="username"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  disabled={Boolean(authBusy)}
                />
              </label>
              <label>
                Password
                <input
                  className="mf-input"
                  type="password"
                  autoComplete="current-password"
                  value={authPass}
                  onChange={(e) => setAuthPass(e.target.value)}
                  disabled={Boolean(authBusy)}
                />
              </label>
              {loginErr ? <p className="mf-login-err" role="alert">{loginErr}</p> : null}
              <div className="mf-login-actions">
                <button type="submit" className="mf-btn primary" disabled={Boolean(authBusy)}>
                  {authBusy === "login" ? "Signing in…" : "Sign in"}
                </button>
                <button
                  type="button"
                  className="mf-btn"
                  disabled={Boolean(authBusy)}
                  onClick={() => void onEmailRegister()}
                >
                  {authBusy === "register" ? "Creating…" : "Register"}
                </button>
              </div>
            </form>
            <button
              type="button"
              className="mf-chip"
              disabled={Boolean(authBusy)}
              onClick={startGoogleLoginWeb}
            >
              <PiGoogleLogoBold /> Continue with Google
            </button>
          </div>
        ) : null}

        {walletOpen && user ? (
          <div className="mf-wallet-pop" role="dialog" aria-label="Suite wallet">
            <div className="mf-wallet-head">
              <span>
                Suite wallet · {wallet?.subscriptionActive ? wallet.subscriptionLabel || "Member" : "Free"}
              </span>
              <button type="button" className="mf-x" onClick={() => setWalletOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="mf-pack-row">
              {(wallet?.packs || [{ amountUsd: 5, label: "$5" }]).map((p) => (
                <button key={p.amountUsd} type="button" onClick={() => buy(p.amountUsd)}>
                  Add {p.label}
                </button>
              ))}
              <button type="button" onClick={member}>
                Member ${SUITE_MEMBER_PRICE_USD}/mo
              </button>
            </div>
          </div>
        ) : null}

        <aside
          className={`mf-rail ${railOpen ? "" : "collapsed"}`}
          hidden={!railOpen}
          aria-hidden={!railOpen}
        >
          <SplitHandle
            axis="x"
            invert
            label="Resize library"
            value={railW}
            min={RAIL_MIN}
            max={480}
            onChange={setRailW}
            onReset={() => setRailOpen(false)}
          />
          <div className="mf-rail-head">
            <h2>Library</h2>
            <button type="button" className="mf-x mf-rail-close" onClick={() => setRailOpen(false)} aria-label="Close library">
              ✕
            </button>
          </div>
          <p className="mf-hint">House Music 3</p>
          <p className="mf-hint">HF · {MUSICFORGE_HF_BUCKET}</p>
          <ul className="mf-takes">
            {takes.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => playTake(t)}
                >
                  {t.title}
                  <small>
                    {t.duration}s · seed {t.seed}
                  </small>
                </button>
                <button
                  type="button"
                  className="mf-seed-btn"
                  onClick={() => applyTakeSeed(t)}
                >
                  Seed
                </button>
              </li>
            ))}
            {!takes.length ? <li className="mf-hint">No takes yet.</li> : null}
          </ul>
          {busy === "gen" ? (
            <button
              type="button"
              className="mf-btn ghost"
              onClick={() => abortRef.current?.abort()}
            >
              Stop
            </button>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
